import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, Play, FileLock2, X } from "lucide-react";
import ArtLightbox from "../components/ArtLightbox";
import VisualArchive from "../components/VisualArchive";

/* ===========================================================
   CHERUBIM · premium franchise presentation
   Black · White · Gold · Deep Royal Purple
   No phone fields. No stock photos of people.
   =========================================================== */

const SECTION_TITLE = "font-caps-wide text-[12px] md:text-[13px] text-[var(--cb-gold)] text-center";
const SECTION_WRAP = "max-w-[1100px] mx-auto px-6 md:px-8";
const BODY_PROSE = "font-body text-[16px] md:text-[17px] text-[var(--cb-text)] leading-[1.8]";

const SectionEyebrow = ({ children }) => <div className={SECTION_TITLE}>{children}</div>;
const Rule = () => <div className="gold-rule" />;

const EASE = [0.16, 1, 0.3, 1];

/* Scroll-reveal wrapper — fades + rises once when entering the viewport */
const Reveal = ({ children, className = "", delay = 0, y = 28 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

/* Masked line-by-line reveal — signature on-load moment */
const MaskedLine = ({ children, delay = 0 }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  </div>
);

/* ---------- DATA ---------- */

const CHARACTERS = [
  { name: "CHASE MICHAELS", role: "THE STANDING MAN",
    image: "/assets/art/chase-michaels.jpg",
    imagePosition: "28% 22%",
    altImage: "/assets/art/guardian-card.jpg",
    body: "A decorated former elite soldier, wrongfully convicted, searching for truth, purpose, and redemption." },
  { name: "MONK", role: "THE FALLEN BROTHER",
    image: "/assets/art/monk-portrait.jpg",
    imagePosition: "center 22%",
    body: "Once closer than brothers, Cam and Monk now stand on opposite sides of Memphis — one chose redemption, the other survival." },
  { name: "PASTOR WEI CHEN", role: "THE SPIRITUAL SPINE",
    image: "/assets/art/chen-portrait.jpg",
    imagePosition: "center 25%",
    body: "Mentor. Teacher. The man who helps Cam rebuild his life." },
  { name: "LILLIAN PAIGE", role: "THE QUIET FIRE",
    image: "/assets/art/lillian-analyst-poster.jpg",
    imagePosition: "center 30%",
    body: "Former journalist. Truth seeker." },
  { name: "ANDY REYES", role: "THE BUILDER",
    image: "/assets/art/andy-portrait.jpg",
    imagePosition: "center 22%",
    body: "Inventor of TRINITY. Engineer. Visionary. Architect of the future." },
  { name: "NOAH BROWN", role: "THE FUTURE",
    image: "/assets/art/noah-portrait.jpg",
    imagePosition: "center 12%",
    body: "The young life that reminds everyone what is truly at stake." },
  { name: "COLONEL RICHARD HARLAN", role: "THE ARCHITECT",
    image: "/assets/art/harlan-portrait.jpg",
    imagePosition: "center 20%",
    body: "A strategist who believes control is more important than freedom." },
];

const WORLD = [
  { name: "MEMPHIS · THE EIGHTH CHARACTER",
    image: "/assets/art/world-memphis.jpg", fit: "contain",
    body: "Memphis is more than a location. It is a living force within the story. Its churches. Its neighborhoods. Its history. Its wounds. Its people. The city is not the backdrop — the city is a character." },
  { name: "THE OVERLOOK",
    image: "/assets/art/world-overlook.jpg",
    body: "An estate overlooking the city. Part sanctuary. Part headquarters. Part inheritance." },
  { name: "THE HOPE CENTER",
    image: "/assets/art/world-hopecenter.jpg",
    body: "Pastor Chen's refuge for people seeking a second chance. The emotional heart of the franchise." },
  { name: "THE CHOIRHOUSE",
    image: "/assets/art/world-choirhouse.jpg",
    body: "Monk's headquarters. A fortress built around loyalty, family, and survival." },
  { name: "IRONVALE FEDERAL PRISON",
    image: "/assets/art/world-ironvale.jpg",
    body: "The place that tried to break Chase Michaels. The place that unknowingly forged him." },
  { name: "ALMER'S MARKET",
    image: "/assets/art/world-almers-market.jpg",
    imagePosition: "center top",
    body: "Elijah Almer's storefront — the surface of a deeper legacy. The first place Cam returns to when the city forgets his name." },
];

const SYSTEMS = [
  { name: "CHERUBIM",
    sub: "Advanced Protective Armor",
    body: "Built around responsibility rather than destruction. A platform designed to protect. Not conquer.",
    image: "/assets/art/tech-cherubim.jpg" },
  { name: "TRINITY",
    sub: "Scripture-Coded Intelligence",
    body: "Created by Andy Reyes to preserve truth, protect life, and support human choice. It is not God. Faith directs it.",
    image: "/assets/art/tech-trinity.jpg" },
];

/* Choir visual roster — order locked by owner-approved lineup artwork:
   ZEE → BOOM → KILLA → MONK → PEACHES → BRICKCITY → CAMO (Monk centered).
   Members are identified by NAME ONLY until role titles are verified
   against the authoritative CHERUBIM package. */
/* Lower Choir member grid — six future individual portrait cards.
   Monk is intentionally EXCLUDED here (fully established above as leader);
   order follows the owner-locked group artwork minus Monk.
   NAME ONLY until owner supplies approved portraits/roles. */
const CHOIR = [
  { name: "ZEE", image: "/assets/art/choir-zee.jpg" },
  { name: "BOOM", image: "/assets/art/choir-boom.jpg" },
  { name: "KILLA", image: "/assets/art/choir-killa.jpg" },
  { name: "PEACHES", image: "/assets/art/choir-peaches.jpg" },
  { name: "BRICKCITY", image: "/assets/art/choir-brickcity.jpg" },
  { name: "CAMO", image: "/assets/art/choir-camo.jpg" },
];

const CHOIR_ORDER = ["ZEE", "BOOM", "KILLA", "MONK", "PEACHES", "BRICKCITY", "CAMO"];

const INITIATIVE = [
  { name: "PASTOR WEI CHEN", role: "Mentor", body: "The spiritual foundation of the Initiative.",
    image: "/assets/art/initiative-chen.jpg" },
  { name: "ANDY REYES", role: "Architect", body: "Inventor of TRINITY and builder of the future.",
    image: "/assets/art/initiative-andy.jpg" },
  { name: "LILLIAN PAIGE", role: "Investigator", body: "A relentless seeker of truth.",
    image: "/assets/art/lillian-poster.jpg", fit: "contain" },
  { name: "NOAH BROWN", role: "The Future", body: "The next generation worth protecting.",
    image: "/assets/art/initiative-noah.jpg" },
  { name: "TRINITY", role: "Mission Intelligence", body: "The scripture-coded intelligence connecting the entire Initiative.",
    image: "/assets/art/trinity-protocols.jpg", landscape: true },
  { name: "THE OVERLOOK", role: "Headquarters", body: "The operational heart of the Cherubim Initiative.",
    image: "/assets/art/initiative-overlook.jpg", landscape: true },
];

const FRANCHISE = [
  { code: "CHERUBIM", name: "Cherubim", tag: "Flagship Series",
    image: "/assets/art/franchise-cherubim.jpg",
    tint: "rgba(184,138,44,0.40)", accent: "rgba(232,199,102,0.30)" },
  { code: "REDEEMED SOUL MONK", name: "RSM", full: "Redeemed Soul Monk", tag: "Future Universe Expansion",
    image: "/assets/art/franchise-rsm.jpg", imagePosition: "center 18%",
    tint: "rgba(74,31,92,0.55)", accent: "rgba(160,106,184,0.30)" },
  { code: "SERAPHIM", name: "Seraphim", tag: "Future Universe Expansion",
    image: "/assets/art/franchise-seraphim.jpg",
    tint: "rgba(20,48,92,0.55)", accent: "rgba(80,140,220,0.30)" },
  { code: "THE FINAL PROTOCOL", name: "Cherubim · The Final Protocol", tag: "Feature Film Expansion",
    image: "/assets/art/franchise-final-protocol.jpg", fit: "contain",
    tint: "rgba(18,30,40,0.65)", accent: "rgba(140,170,200,0.25)" },
  { code: "PRECIOUS METALS", name: "Precious Metals · Silver & Gold", tag: "Future Universe Expansion",
    image: "/assets/art/franchise-precious-metals.jpg", fit: "contain",
    tint: "rgba(40,40,48,0.55)", accent: "rgba(212,192,130,0.30)" },
  { code: "LEGEND OF THE IRON PALM", name: "Legend Of The Iron Palm", tag: "Future Universe Expansion",
    image: "/assets/art/franchise-iron-palm.jpg", imagePosition: "center 30%",
    tint: "rgba(72,36,12,0.55)", accent: "rgba(184,138,44,0.35)" },
  { code: "REYES", name: "Reyes", tag: "Future Universe Expansion",
    image: "/assets/art/andy-portrait.jpg", imagePosition: "center 30%",
    tint: "rgba(14,36,58,0.55)", accent: "rgba(58,124,180,0.32)" },
];

/* Estate of Elijah Almer — inheritance document archive.
   Titles reflect only what each document actually shows.
   Organized as a dossier library the visitor enters via one entry point. */
const ARCHIVE_GROUPS = [
  { group: "ESTATE TRANSFER / LEGAL", docs: [
    { key: "toc", title: "Estate Transfer Portfolio · Complete Schedule", type: "Table Of Contents" },
    { key: "business-transfer", title: "Business Asset Transfer", type: "Legal Instrument" },
  ]},
  { group: "THE OVERLOOK", docs: [
    { key: "overlook-overview", title: "The Overlook Estate · Property Overview", type: "Estate Record" },
  ]},
  { group: "PROPERTY & DEEDS", docs: [
    { key: "exhibit-a", title: "Real Estate Holdings", type: "Exhibit A" },
    { key: "warehouse", title: "Almer's Market Warehouse", type: "Property Record" },
  ]},
  { group: "MAPS / SITE PLANS", docs: [
    { key: "property-map", title: "Property Location Map", type: "Map" },
  ]},
  { group: "LETTERS / CORRESPONDENCE", docs: [
    { key: "letter-children", title: "Letter To My Children", type: "Attachment 21" },
    { key: "exhibit-e", title: "Personal Correspondence", type: "Exhibit E" },
  ]},
  { group: "EXHIBITS / SUPPORTING RECORDS", docs: [
    { key: "exhibit-b", title: "Vehicle Inventory", type: "Exhibit B" },
    { key: "exhibit-c", title: "Business Operations", type: "Exhibit C" },
    { key: "exhibit-d", title: "Financial Holdings", type: "Exhibit D" },
    { key: "vehicle-inventory", title: "Estate Vehicle Inventory", type: "Inventory Record" },
  ]},
];

const DOC_URL = (key) => `/assets/docs/${key}.jpg`;

/* ---------- EDITORIAL MARQUEE ---------- */
const MARQUEE_ITEMS = [
  "FAITH-TECH THRILLER",
  "THEY TRIED TO ERASE HIS NAME",
  "HEAVEN REMEMBERS",
  "MEMPHIS",
  "TRUTH HAS A GUARDIAN",
  "ASHECRAFT INTERNATIONAL",
];

const EditorialMarquee = () => (
  <div data-testid="editorial-marquee"
       className="border-y border-[var(--cb-divider)] py-5 overflow-hidden select-none"
       style={{ background: "#070709" }}>
    <div className="cb-marquee-track flex w-max">
      {[0, 1].map((copy) => (
        <div key={copy} className="flex items-center whitespace-nowrap" aria-hidden={copy === 1}>
          {MARQUEE_ITEMS.concat(MARQUEE_ITEMS).map((item, i) => (
            <span key={`${copy}-${i}`} aria-hidden={copy === 1 || i >= MARQUEE_ITEMS.length}
                  className="font-caps-wide text-[11px] md:text-[12px] text-[var(--cb-text-dim)] mx-7 inline-flex items-center gap-14">
              {item}
              <span className="inline-block w-[5px] h-[5px] rotate-45 bg-[var(--cb-gold)] opacity-70" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

/* ---------- CHARACTER CARD ----------
   Cards with an altImage (Chase Michaels) support a restrained
   Cam → CHERUBIM manifestation: desktop hover or mobile tap/keyboard. */
const CharacterCard = ({ c, i }) => {
  const [manifest, setManifest] = useState(false);
  const [touched, setTouched] = useState(false);
  const interactive = Boolean(c.altImage);
  const toggle = interactive ? () => { setTouched(true); setManifest((m) => !m); } : undefined;

  return (
    <Reveal delay={(i % 4) * 0.08}>
      <article
        data-testid={`character-card-${c.name.replace(/[^A-Za-z]/g, '-').toLowerCase()}`}
        className="cb-card flex flex-col overflow-hidden group transition-transform duration-500 hover:-translate-y-1 h-full"
      >
        <div
          className={`relative w-full aspect-[3/4] overflow-hidden bg-[var(--cb-bg-3)] ${interactive ? "cursor-pointer" : ""}`}
          onClick={toggle}
          {...(interactive ? {
            role: "button", tabIndex: 0, "aria-pressed": manifest,
            "aria-label": "Reveal Chase Michaels manifested as CHERUBIM",
            "data-testid": "cam-manifest-toggle",
            onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } },
          } : {})}
        >
          <img src={c.image} alt={c.name} loading="lazy"
               style={c.imagePosition ? { objectPosition: c.imagePosition } : undefined}
               className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
          {interactive && (
            <>
              <img src={c.altImage} alt="Chase Michaels manifested as CHERUBIM" loading="lazy"
                   data-testid="cherubim-reveal-img"
                   className={`absolute inset-0 w-full h-full object-cover transition-all duration-[1600ms] ease-out ${manifest ? "opacity-100 scale-100 blur-0" : "opacity-0 scale-[1.12] blur-[8px]"} md:group-hover:opacity-100 md:group-hover:scale-100 md:group-hover:blur-0`} />
              {touched && (
                <div key={manifest ? "manifest-on" : "manifest-off"}
                     data-testid="manifest-flash"
                     className="absolute inset-0 pointer-events-none animate-cb-flash"
                     style={{ background: "radial-gradient(circle at 50% 42%, rgba(232,199,102,0.6) 0%, rgba(160,106,184,0.25) 40%, transparent 65%)" }} />
              )}
              <div data-testid="manifest-hint"
                   className={`absolute bottom-3 right-3 font-caps text-[8px] tracking-[0.18em] px-2.5 py-1.5 border backdrop-blur transition-colors duration-700 ${manifest ? "text-black bg-[var(--cb-gold)] border-[var(--cb-gold)]" : "text-[var(--cb-gold)] bg-black/60 border-[var(--cb-gold)]/30"}`}>
                <span className="hidden md:inline">HOVER</span><span className="md:hidden">TAP</span> TO EXPLORE
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-5 flex-1 flex flex-col">
          <h3 className="font-headline text-[var(--cb-gold)] text-[18px] md:text-[20px] uppercase tracking-[0.03em] leading-tight">{c.name}</h3>
          <div className="font-caps text-[10px] text-[var(--cb-text-dim)] mt-2">{c.role}</div>
          <p className="font-serif text-[14.5px] text-[var(--cb-text)] leading-[1.7] mt-4">{c.body}</p>
        </div>
      </article>
    </Reveal>
  );
};

/* =========================================================== */

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const [choirArt, setChoirArt] = useState(null);
  const [premiseArtOpen, setPremiseArtOpen] = useState(false);
  const [teaserOpen, setTeaserOpen] = useState(false);

  /* While the teaser plays, the score ducks; closing brings it back */
  useEffect(() => {
    window.dispatchEvent(new Event(teaserOpen ? "cherubim:pause-score" : "cherubim:resume-score"));
  }, [teaserOpen]);

  useEffect(() => {
    const syncHeroAccessibility = () => {
      const desktop = window.matchMedia("(min-width: 1024px)").matches;
      document.querySelectorAll("[data-hero-copy]").forEach((el) => {
        const variant = el.getAttribute("data-hero-copy");
        el.setAttribute("aria-hidden", String((variant === "desktop") !== desktop));
      });
    };
    syncHeroAccessibility();
    window.addEventListener("resize", syncHeroAccessibility);
    return () => window.removeEventListener("resize", syncHeroAccessibility);
  }, []);


  return (
    <div data-testid="page-home" className="bg-[var(--cb-bg)]">

      {/* ============ HERO ============ */}
      <section id="hero" data-testid="section-hero" ref={heroRef} className="relative min-h-[100svh] flex flex-col overflow-hidden">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          {/* Mobile portrait: tall crop — Guardian screen-dominant (+4%), shifted
              slightly up, face and chest cross clear of all copy */}
          <img
            src="/assets/art/hero-still-mobile.jpg"
            alt="CHERUBIM — the winged Guardian standing watch over Memphis"
            data-testid="hero-keyart-mobile"
            className="hidden max-lg:portrait:block absolute"
            style={{ height: "104%", width: "auto", maxWidth: "none", left: "50%", top: "-18px", transform: "translateX(-50%)" }}
          />
          {/* Landscape below desktop width (phone sideways, narrow windows): wide
              cinematic key art, full-bleed — same treatment as the desktop hero */}
          <img
            src="/assets/art/hero-still-wide.jpg"
            alt="" aria-hidden="true"
            data-testid="hero-keyart-landscape"
            className="hidden max-lg:landscape:block absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "64% 0%" }}
          />
          {/* Desktop: wide cinematic key art, full-bleed — Guardian, Shadrach,
              Memphis skyline and helicopter all hold frame; copy sits in the
              left negative space over a dark gradient */}
          <img
            src="/assets/art/hero-still-wide.jpg"
            alt="CHERUBIM — the winged Guardian standing watch over Memphis"
            data-testid="hero-keyart"
            className="hidden lg:block absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "64% 42%" }}
          />
          <div className="hidden lg:block max-lg:landscape:block absolute inset-y-0 left-0 w-[62%] pointer-events-none"
               style={{ background: "linear-gradient(90deg, rgba(8,8,12,0.94) 0%, rgba(8,8,12,0.72) 42%, rgba(8,8,12,0.25) 72%, transparent 100%)" }} />
        </motion.div>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 right-0 h-[42%]" style={{
            background: "linear-gradient(180deg, rgba(8,8,14,0.88) 0%, rgba(8,8,14,0.55) 40%, rgba(8,8,14,0.18) 75%, transparent 100%)"
          }} />
          <div className="absolute bottom-0 left-0 right-0 h-[52%]" style={{
            background: "linear-gradient(0deg, #0A0A0A 0%, rgba(10,10,10,0.92) 25%, rgba(10,10,10,0.55) 55%, transparent 100%)"
          }} />
          <div className="absolute inset-0" style={{
            background: "radial-gradient(ellipse at 50% 100%, rgba(184,138,44,0.10) 0%, transparent 50%)"
          }} />
        </div>

        {/* TOP — branded mark */}
        <div className={`relative z-10 ${SECTION_WRAP} text-center pt-10 sm:pt-12 hidden max-lg:portrait:block`} data-hero-copy="mobile-top">
          <MaskedLine delay={0.15}>
            <div className="cb-logo-subtitle text-[9px] sm:text-[10px]">
              ASHECRAFT INTERNATIONAL PRESENTS
            </div>
          </MaskedLine>
          <MaskedLine delay={0.32}>
            <h1 className="mt-3">
              <img
                src="/assets/art/cherubim-logo.png"
                alt="CHERUBIM"
                data-testid="hero-title-mobile"
                className="mx-auto h-auto w-[min(76vw,660px)]"
              />
            </h1>
          </MaskedLine>
          <MaskedLine delay={0.52}>
            <div className="cb-logo-subtitle text-[10px] sm:text-[12px] md:text-[13px] mt-2" data-testid="hero-sub">
              FAITH-TECH THRILLER
            </div>
          </MaskedLine>
        </div>

        <div className="flex-1" />

        {/* BOTTOM — tagline, hook, CTAs */}
        <div className={`relative z-10 ${SECTION_WRAP} text-center pb-5 sm:pb-16 md:pb-20 hidden max-lg:portrait:block`} data-hero-copy="mobile-bottom">
          <MaskedLine delay={0.72}>
            <p
              data-testid="hero-tagline"
              className="font-headline text-[var(--cb-text)] text-[22px] sm:text-[28px] md:text-[34px] leading-[1.15] tracking-[0.02em] uppercase"
            >
              They Tried To Erase His Name.
            </p>
          </MaskedLine>
          <MaskedLine delay={0.86}>
            <p className="font-headline text-[var(--cb-gold)] text-[22px] sm:text-[28px] md:text-[34px] leading-[1.15] tracking-[0.02em] uppercase">
              Heaven Remembers.
            </p>
          </MaskedLine>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1, ease: EASE }}
          >
            <p
              data-testid="hero-hook"
              className="font-serif italic text-[var(--cb-text-dim)] text-[13px] leading-snug sm:text-[17px] md:text-[18px] sm:leading-relaxed max-w-[300px] sm:max-w-[680px] mx-auto mt-4 sm:mt-6"
            >
              A premium faith-tech thriller about truth, redemption, conspiracy, and the cost of power.
            </p>

            <div className="mt-5 sm:mt-8 flex flex-row flex-wrap items-center justify-center gap-2 sm:gap-4 max-w-md sm:max-w-none mx-auto">
              <button
                data-testid="cta-watch-teaser"
                onClick={() => setTeaserOpen(true)}
                className="btn-primary-cb max-sm:px-2.5 max-sm:py-3 max-sm:text-[9px]"
              >
                <Play size={14} /> Watch Teaser
              </button>
              <a
                href="#cherubim-initiative" data-testid="cta-enter-universe" className="btn-outline-cb max-sm:px-2.5 max-sm:py-3 max-sm:text-[9px]"
                onClick={(e) => { e.preventDefault(); document.getElementById('cherubim-initiative')?.scrollIntoView({ behavior: 'smooth' }); }}
              >
                Enter The Universe <ArrowRight size={13} />
              </a>
            </div>

            <div className="font-caps-wide text-[8px] sm:text-[10px] text-[var(--cb-gold)] mt-5 sm:mt-10">
              FIRST OF THE TRINITY SAGA · A FOUR-SEASON PRESTIGE DRAMA
            </div>
          </motion.div>
        </div>

        {/* DESKTOP + landscape band — left negative-space rail; complete artwork stands clear on the right */}
        <div className="hidden lg:flex max-lg:landscape:flex absolute inset-0 z-10 items-center" data-hero-copy="desktop">
          <div className="max-w-[46%] pl-16 xl:pl-24 max-lg:landscape:max-w-[58%] max-lg:landscape:pl-8">
            <MaskedLine delay={0.15}>
              <div className="cb-logo-subtitle text-[10px]">ASHECRAFT INTERNATIONAL PRESENTS</div>
            </MaskedLine>
            <MaskedLine delay={0.32}>
              <h1 className="mt-4 max-lg:landscape:mt-2">
                <img
                  src="/assets/art/cherubim-logo.png"
                  alt="CHERUBIM"
                  data-testid="hero-title"
                  className="h-auto w-[420px] xl:w-[520px] max-lg:landscape:w-[240px]"
                />
              </h1>
            </MaskedLine>
            <MaskedLine delay={0.5}>
              <div className="cb-logo-subtitle text-[11px] xl:text-[12px] mt-3 max-lg:landscape:mt-1.5" data-testid="hero-sub-desktop">
                FAITH-TECH THRILLER
              </div>
            </MaskedLine>
            <MaskedLine delay={0.68}>
              <p data-testid="hero-tagline-desktop"
                 className="font-headline text-[var(--cb-text)] text-[26px] xl:text-[34px] leading-[1.15] tracking-[0.02em] uppercase mt-10 xl:mt-14 max-lg:landscape:text-[19px] max-lg:landscape:mt-4">
                They Tried To Erase His Name.
              </p>
            </MaskedLine>
            <MaskedLine delay={0.82}>
              <p className="font-headline text-[var(--cb-gold)] text-[26px] xl:text-[34px] leading-[1.15] tracking-[0.02em] uppercase max-lg:landscape:text-[19px]">
                Heaven Remembers.
              </p>
            </MaskedLine>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.05, ease: EASE }}
            >
              <p className="font-serif italic text-[var(--cb-text-dim)] text-[15px] xl:text-[16px] leading-relaxed max-w-[440px] mt-5 max-lg:landscape:text-[12px] max-lg:landscape:leading-snug max-lg:landscape:mt-3">
                A premium faith-tech thriller about truth, redemption, conspiracy, and the cost of power.
              </p>
              <div className="mt-7 flex flex-row flex-wrap items-center gap-4 max-lg:landscape:mt-4">
                <button
                  data-testid="cta-watch-teaser-desktop"
                  onClick={() => setTeaserOpen(true)}
                  className="btn-primary-cb"
                >
                  <Play size={14} /> Watch Teaser
                </button>
                <a
                  href="#cherubim-initiative" data-testid="cta-enter-universe-desktop" className="btn-outline-cb"
                  onClick={(e) => { e.preventDefault(); document.getElementById('cherubim-initiative')?.scrollIntoView({ behavior: 'smooth' }); }}
                >
                  Enter The Universe <ArrowRight size={13} />
                </a>
              </div>
              <div className="font-caps-wide text-[10px] text-[var(--cb-gold)] mt-9 max-lg:landscape:mt-4">
                FIRST OF THE TRINITY SAGA · A FOUR-SEASON PRESTIGE DRAMA
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ EDITORIAL MARQUEE ============ */}
      <EditorialMarquee />

      {/* ============ THE CHERUBIM INITIATIVE — ACCESS LIST ============ */}
      <CherubimInitiativeSection />

      {/* ============ CHERUBIM PREVIEW VIDEO ============ */}
      <SeriesVideoSection />

      {/* ============ THE SERIES (concise) ============ */}
      <section id="the-series" data-testid="section-series" className="pt-8 pb-24 md:pt-12 md:pb-28">
        <Reveal className={SECTION_WRAP}>
          <SectionEyebrow>THE SERIES</SectionEyebrow>
          <h2 className="font-headline text-[var(--cb-text)] text-[32px] md:text-[44px] uppercase tracking-[0.03em] text-center mt-5">
            What is CHERUBIM?
          </h2>
          <div className="font-caps text-[11px] text-[var(--cb-gold)] text-center mt-3">
            FAITH-TECH THRILLER
          </div>
          <div className="font-serif italic text-[var(--cb-text-dim)] text-center text-[14px] md:text-[15px] mt-1">
            Conspiracy Thriller · Faith-Tech Storytelling · Modern Mythology
          </div>
          <Rule />
          <div className="max-w-[720px] mx-auto mt-8 space-y-6 text-center">
            <p className={BODY_PROSE}>
              CHERUBIM is a premium faith-tech thriller about truth, redemption, conspiracy, and the cost of power.
            </p>
            <p className={BODY_PROSE}>
              Set in modern Memphis, the series follows Chase “Cam” Michaels, a decorated former elite soldier whose life is taken by a manufactured conviction — and returned through an extraordinary release.
            </p>
            <p className={BODY_PROSE}>
              As Cam searches for answers, the world he inherits becomes a mystery he must learn to protect.
            </p>
            <p className="font-headline text-[var(--cb-text)] text-[18px] md:text-[22px] uppercase tracking-[0.04em] pt-2">
              What begins as a search for truth becomes a battle over who controls it.
            </p>
            <p className="font-headline text-[var(--cb-gold)] text-[18px] md:text-[22px] uppercase tracking-[0.04em]">
              And at the center of that battle rises a guardian known as CHERUBIM.
            </p>
          </div>

          {/* THE MAN UNDERNEATH — visual anchor */}
          <div className="grid md:grid-cols-2 gap-5 mt-14 max-w-[980px] mx-auto">
            <Reveal>
              <div className="overflow-hidden aspect-video" style={{ background: "var(--cb-bg-2)" }}>
                <img
                  src="/assets/art/series-cam.jpg"
                  alt="Chase Michaels on a Memphis street — the man underneath the mythology"
                  loading="lazy"
                  data-testid="series-visual-cam"
                  className="block w-full h-full object-contain"
                />
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="overflow-hidden aspect-video" style={{ background: "var(--cb-bg-2)" }}>
                <img
                  src="/assets/art/series-soldier.jpg"
                  alt="Chase Alexander Michaels — decorated former elite soldier"
                  loading="lazy"
                  data-testid="series-visual-soldier"
                  className="block w-full h-full object-contain"
                />
              </div>
              <div className="p-4 text-center">
                <div className="font-caps text-[10px] text-[var(--cb-gold)]">THE DECORATED SOLDIER</div>
                <div className="font-serif italic text-[var(--cb-text-dim)] text-[13px] mt-2">Before the city forgot his name.</div>
              </div>
            </Reveal>
          </div>

          {/* KEY THEMES */}
          <div className="mt-14">
            <div className="font-caps-wide text-[11px] text-[var(--cb-gold)] text-center">KEY THEMES</div>
            <div className="h-px w-10 bg-[var(--cb-gold)] mt-4 mx-auto" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 max-w-4xl mx-auto mt-8">
              {[
                "Truth vs Manipulation",
                "Redemption vs Revenge",
                "Faith vs Fear",
                "Service vs Power",
                "Legacy vs Corruption",
              ].map((t) => (
                <div key={t} className="cb-card-static p-4 text-center">
                  <div className="font-caps text-[10px] text-[var(--cb-gold)]">{t}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* ============ ENTER THE STORY (animated lines) ============ */}
      <EnterTheStorySection />

      {/* ============ THE PREMISE ============ */}
      <section id="the-premise" data-testid="section-premise"
               className="pt-8 pb-24 md:pt-12 md:pb-28 border-t border-[var(--cb-divider)]"
               style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(74,31,92,0.18) 0%, transparent 60%), var(--cb-bg)" }}>
        <div className={SECTION_WRAP}>
          <SectionEyebrow>THE PREMISE</SectionEyebrow>
          <Rule />
          <Reveal className="max-w-[980px] mx-auto mt-10">
            <button
              type="button"
              data-testid="premise-ironvale-button"
              aria-label="Open IronVale escort artwork full-screen"
              onClick={() => setPremiseArtOpen(true)}
              className="group relative block w-full cursor-zoom-in overflow-hidden text-left"
            >
              <img
                src="/assets/art/ironvale-escort.jpg"
                alt="Chase Michaels escorted by military police in the rain outside IronVale Federal Correctional Institution"
                loading="lazy"
                data-testid="premise-ironvale-img"
                className="block w-full h-auto transition-transform duration-[1200ms] ease-out group-hover:scale-[1.02] group-active:scale-[0.995]"
              />
              <div className="absolute inset-x-0 bottom-0 px-5 pb-4 pt-12 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                   style={{ background: "linear-gradient(0deg, rgba(0,0,0,0.78) 0%, transparent 100%)" }}>
                <span className="block font-caps text-[9px] text-[var(--cb-gold)] text-center">TAP FOR FULL HD</span>
              </div>
            </button>
          </Reveal>
          <ArtLightbox
            active={premiseArtOpen ? { src: "/assets/art/ironvale-escort.jpg", label: "THE PREMISE", sub: "IRONVALE ESCORT" } : null}
            onClose={() => setPremiseArtOpen(false)}
            testid="premise-lightbox"
          />
          <div className="max-w-[640px] mx-auto mt-10 text-center space-y-5">
            {[
              "A decorated soldier is framed.",
              "A city forgets him.",
              "A mentor leaves him everything.",
              "A conspiracy wants him erased.",
            ].map((line, i) => (
              <Reveal key={line} delay={i * 0.12}>
                <p className="font-headline text-[var(--cb-text)] text-[20px] md:text-[26px] uppercase tracking-[0.04em]">
                  <span className="font-caps text-[var(--cb-gold)] text-[11px] md:text-[12px] align-middle mr-4 tracking-[0.3em]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {line}
                </p>
              </Reveal>
            ))}
            <Reveal delay={0.55}>
              <p className="font-headline text-[var(--cb-gold)] text-[24px] md:text-[32px] uppercase tracking-[0.05em] pt-2">
                A guardian rises.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ WHY IT MATTERS ============ */}
      <section id="why-it-matters" data-testid="section-why" className="py-24 border-t border-[var(--cb-divider)]">
        <Reveal className={SECTION_WRAP}>
          <SectionEyebrow>WHY IT MATTERS</SectionEyebrow>
          <Rule />
          <Reveal className="max-w-[880px] mx-auto mt-10">
            <img
              src="/assets/art/why-truth.jpg"
              alt="Truth is unstable — Cam's face dissolving into the lights of Memphis"
              loading="lazy"
              data-testid="why-truth-img"
              className="block w-full h-auto"
            />
          </Reveal>
          <div className="max-w-[760px] mx-auto mt-10 text-center space-y-6">
            <p className="font-serif text-[var(--cb-text-dim)] text-[18px] md:text-[20px] leading-snug">
              Most heroes gain power and become stronger. CHERUBIM asks a different question:
            </p>
            <p className="font-headline text-[var(--cb-gold)] text-[24px] md:text-[32px] uppercase tracking-[0.04em] leading-tight">
              What kind of person should possess power in the first place?
            </p>
            <p className={BODY_PROSE}>
              Every victory has a cost. Every choice has consequences. Every act of strength is measured against character.
            </p>
          </div>
        </Reveal>
      </section>

      {/* ============ THE HEART OF CHERUBIM ============ */}
      <section id="the-heart" data-testid="section-heart"
               className="py-24 border-t border-[var(--cb-divider)]"
               style={{ background: "radial-gradient(ellipse at 20% 50%, rgba(184,138,44,0.10) 0%, transparent 55%), radial-gradient(ellipse at 80% 50%, rgba(74,31,92,0.16) 0%, transparent 55%), var(--cb-bg)" }}>
        <div className={SECTION_WRAP}>
          <SectionEyebrow>THE HEART OF CHERUBIM</SectionEyebrow>
          <Rule />
          <Reveal className="max-w-[1100px] mx-auto mt-10">
            <button type="button"
                    data-testid="heart-boys-enlarge"
                    onClick={() => setChoirArt({ src: "/assets/art/brothers-boys.jpg", label: "THE HEART OF CHERUBIM", sub: "Chase & Monk — Orange Mound, Memphis" })}
                    aria-label="Enlarge the two boys Orange Mound artwork"
                    className="block w-full cursor-pointer">
              <img
                src="/assets/art/brothers-boys.jpg"
                alt="Chase and Monk as boys — Orange Mound, Memphis"
                loading="lazy"
                data-testid="heart-faceoff-img"
                className="block w-full h-auto"
              />
            </button>
          </Reveal>
          <Reveal className="max-w-[720px] mx-auto mt-12 space-y-5 text-center">
            <p className={BODY_PROSE}>Before there was Cherubim, there were two boys.</p>
            <div className="grid grid-cols-2 gap-4 max-w-md mx-auto py-3">
              {["Same streets.", "Same struggles.", "Same dreams.", "Same pain."].map((l) => (
                <p key={l} className="font-headline text-[var(--cb-text)] text-[16px] md:text-[18px] uppercase tracking-[0.03em]">{l}</p>
              ))}
            </div>
            <p className="font-headline text-[var(--cb-gold)] text-[20px] md:text-[24px] uppercase tracking-[0.04em]">One chose redemption.</p>
            <p className="font-headline text-[20px] md:text-[24px] uppercase tracking-[0.04em]" style={{ color: '#A06AB8' }}>One chose survival.</p>
            <p className={`${BODY_PROSE} pt-3`}>
              Years later, Cam and Monk stand on opposite sides of Memphis.
            </p>
            <p className="font-serif italic text-[var(--cb-text-dim)] text-[18px] md:text-[20px] leading-snug pt-2">
              Their conflict is not good versus evil. It is two brothers who chose different roads.
            </p>
            <p className="font-headline text-[var(--cb-gold)] text-[18px] md:text-[22px] uppercase tracking-[0.04em] pt-4">
              That relationship is the heart of CHERUBIM.
            </p>
          </Reveal>
          <Reveal className="max-w-[1100px] mx-auto mt-12">
            <img
              src="/assets/art/heart-faceoff.jpg"
              alt="Chase Michaels and Reed Sinclair Monk — two brothers, two paths"
              loading="lazy"
              className="block w-full h-auto"
            />
          </Reveal>
        </div>
      </section>

      {/* ============ CHARACTERS ============ */}
      <section id="characters" data-testid="section-characters" className="py-24 border-t border-[var(--cb-divider)]">
        <div className={SECTION_WRAP}>
          <SectionEyebrow>THE CHARACTERS</SectionEyebrow>
          <Rule />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {CHARACTERS.map((c, i) => (
              <CharacterCard key={c.name} c={c} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ============ THE INITIATIVE ============ */}
      <section id="the-initiative" data-testid="section-initiative"
               className="pt-24 pb-8 md:pb-12 border-t border-[var(--cb-divider)]"
               style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(184,138,44,0.16) 0%, transparent 60%), radial-gradient(ellipse at 50% 100%, rgba(184,138,44,0.06) 0%, transparent 60%), var(--cb-bg)" }}>
        <div className={SECTION_WRAP}>
          <SectionEyebrow>THE INITIATIVE</SectionEyebrow>
        </div>
        <Reveal className="max-w-[1440px] mx-auto px-4 md:px-8 mt-8">
          <img
            src="/assets/art/initiative-foundation.jpg"
            alt="The Foundation Behind Him — Lillian Paige, Noah Brown, Chase Alexander Michaels, Andy Reyes and Pastor Chen outside the Hope Center"
            loading="lazy"
            data-testid="initiative-foundation-img"
            className="block w-full h-auto aspect-[1672/941] object-cover"
            style={{ background: "var(--cb-bg-2)" }}
          />
        </Reveal>
        <div className={SECTION_WRAP}>
          <h2 className="font-headline text-[var(--cb-text)] text-[28px] md:text-[40px] uppercase tracking-[0.04em] text-center mt-10">
            The Family Behind Cherubim
          </h2>
          <p className="font-serif italic text-[var(--cb-text-dim)] text-center mt-4 text-[17px] max-w-[760px] mx-auto">
            The people who help Chase Michaels rebuild his life, protect Memphis, and carry the mission forward. More than allies. More than a team. A family united by purpose, truth, and redemption.
          </p>
          <Rule />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-12">
            {INITIATIVE.map((m, i) => (
              <Reveal key={m.name} delay={(i % 4) * 0.08}>
                <article
                         data-testid={`initiative-card-${m.name.replace(/[^A-Za-z]/g, '-').toLowerCase()}`}
                         className="cb-card flex flex-col overflow-hidden group h-full">
                  <div className={`relative w-full ${m.landscape ? "aspect-video" : "aspect-[3/4]"} overflow-hidden bg-[var(--cb-bg-3)]`}>
                    {m.image ? (
                      <button type="button"
                              data-testid={`initiative-enlarge-${m.name.replace(/[^A-Za-z]/g, '-').toLowerCase()}`}
                              onClick={() => setChoirArt({ src: m.image, label: "THE INITIATIVE", sub: m.name })}
                              aria-label={`Enlarge ${m.name} artwork`}
                              className="absolute inset-0 block w-full h-full cursor-pointer">
                        <img src={m.image} alt={m.name} loading="lazy" className={`block w-full h-full ${m.fit === "contain" ? "object-contain" : "object-cover"} transition-transform duration-700 group-hover:scale-[1.04]`} />
                      </button>
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-3"
                           style={{
                             background: "radial-gradient(ellipse at 50% 30%, rgba(232,199,102,0.30) 0%, transparent 60%), radial-gradient(ellipse at 50% 100%, rgba(184,138,44,0.18) 0%, transparent 55%), linear-gradient(180deg, #1A140A 0%, #0A0A0A 100%)"
                           }}>
                        <div className="filigree absolute inset-[20%] opacity-50" />
                        <div className="relative cb-logo text-[18px] sm:text-[22px]" style={{ letterSpacing: '0.08em' }}>{m.name.split(' ')[0]}</div>
                      </div>
                    )}
                  </div>
                  <div className="px-4 py-4">
                    <div className="font-headline text-[var(--cb-gold)] text-[15px] md:text-[16px] uppercase tracking-[0.04em] leading-tight">{m.name}</div>
                    <div className="font-caps text-[9px] text-[var(--cb-text-dim)] mt-1.5">{m.role}</div>
                    <p className="font-serif text-[14px] text-[var(--cb-text)] leading-[1.65] mt-3">{m.body}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ THE CHOIR ============ */}
      <section id="the-choir" data-testid="section-choir"
               className="pt-8 pb-24 md:pt-12 md:pb-24 border-t border-[var(--cb-divider)]"
               style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(74,31,92,0.28) 0%, transparent 60%), radial-gradient(ellipse at 50% 100%, rgba(120,30,30,0.16) 0%, transparent 60%), var(--cb-bg)" }}>
        <div className={SECTION_WRAP}>
          <SectionEyebrow>THE CHOIR</SectionEyebrow>
          <h2 className="font-headline text-[var(--cb-text)] text-[28px] md:text-[40px] uppercase tracking-[0.04em] text-center mt-5">
            Monk's Family
          </h2>
          <p className="font-serif italic text-[var(--cb-text-dim)] text-center mt-4 text-[17px] max-w-[760px] mx-auto">
            Not a gang. A family. Built by Reed Sinclair Monk from people the world overlooked. Bound by loyalty, protection, and belonging.
          </p>
          <Rule />
          <Reveal className="max-w-[1100px] mx-auto mt-10">
            {/* Desktop: complete wide composition, all seven visible — tap to enlarge */}
            <button type="button"
                    data-testid="choir-lineup-enlarge"
                    onClick={() => setChoirArt({ src: "/assets/art/choir-lineup.jpg", label: "THE CHOIR", sub: "Zee · Boom · Killa · Monk · Peaches · BrickCity · Camo" })}
                    aria-label="Enlarge the Choir lineup artwork"
                    className="hidden md:block w-full cursor-pointer">
              <img
                src="/assets/art/choir-lineup.jpg"
                alt="The Choir — Zee, Boom, Killa, Monk, Peaches, BrickCity and Camo on the Memphis riverfront"
                loading="lazy"
                data-testid="choir-lineup-img"
                className="block w-full h-auto"
              />
            </button>
            {/* Mobile: panoramic pan — nobody gets cropped off the ends; tap opens full-screen */}
            <div className="md:hidden -mx-6 px-6 overflow-x-auto" data-testid="choir-lineup-pan">
              <button type="button"
                      data-testid="choir-lineup-enlarge-mobile"
                      onClick={() => setChoirArt({ src: "/assets/art/choir-lineup.jpg", label: "THE CHOIR", sub: "Zee · Boom · Killa · Monk · Peaches · BrickCity · Camo" })}
                      aria-label="Enlarge the Choir lineup artwork"
                      className="block cursor-pointer">
                <img
                  src="/assets/art/choir-lineup.jpg"
                  alt="The Choir — Zee, Boom, Killa, Monk, Peaches, BrickCity and Camo on the Memphis riverfront"
                  loading="lazy"
                  data-testid="choir-lineup-img-mobile"
                  className="h-[320px] w-auto max-w-none"
                />
              </button>
            </div>
            <div className="md:hidden font-caps text-[9px] text-[var(--cb-text-muted)] text-center mt-3">
              SWIPE TO SEE THE FULL LINEUP · TAP TO ENLARGE
            </div>
            {/* Live name strip — order locked to the artwork, Monk centered */}
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-6" data-testid="choir-order-strip">
              {CHOIR_ORDER.map((n, i) => (
                <span key={n} className="flex items-center gap-4">
                  <span className={`font-caps text-[10px] md:text-[11px] ${n === "MONK" ? "text-[var(--cb-gold)]" : "text-[var(--cb-text-dim)]"}`}>{n}</span>
                  {i < CHOIR_ORDER.length - 1 && <span className="inline-block w-[4px] h-[4px] rotate-45 bg-[var(--cb-gold)] opacity-60" />}
                </span>
              ))}
            </div>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mt-12">
            {CHOIR.map((m, i) => (
              <Reveal key={m.name} delay={(i % 4) * 0.08}>
                <article
                         data-testid={`choir-card-${m.name.replace(/[^A-Za-z]/g, '-').toLowerCase()}`}
                         className="cb-card flex flex-col overflow-hidden group h-full">
                  <div className="relative w-full aspect-[3/4] overflow-hidden bg-[var(--cb-bg-3)]">
                    {m.image ? (
                      <button type="button"
                              data-testid={`choir-enlarge-${m.name.replace(/[^A-Za-z]/g, '-').toLowerCase()}`}
                              onClick={() => setChoirArt({ src: m.image, label: "THE CHOIR", sub: m.name })}
                              aria-label={`Enlarge ${m.name} portrait`}
                              className="absolute inset-0 block w-full h-full cursor-pointer">
                        <img src={m.image} alt={m.name} loading="lazy" className="block w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                      </button>
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-3"
                           style={{
                             background: "radial-gradient(ellipse at 50% 30%, rgba(120,30,30,0.40) 0%, transparent 60%), radial-gradient(ellipse at 50% 100%, rgba(74,31,92,0.35) 0%, transparent 55%), linear-gradient(180deg, #181016 0%, #0A0A0A 100%)"
                           }}>
                        <div className="filigree absolute inset-[20%] opacity-40" />
                        <div className="relative cb-logo text-[18px] sm:text-[22px]" style={{ letterSpacing: '0.10em' }}>{m.name.replace(/"/g, '').split(' ').pop()}</div>
                      </div>
                    )}
                  </div>
                  <div className="px-4 py-4 text-center">
                    <div className="font-headline text-[var(--cb-gold)] text-[15px] md:text-[16px] uppercase tracking-[0.04em] leading-tight">{m.name}</div>
                    {m.role && <div className="font-caps text-[9px] text-[var(--cb-text-dim)] mt-1.5">{m.role}</div>}
                    {m.body && <p className="font-serif text-[14px] text-[var(--cb-text)] leading-[1.65] mt-3">{m.body}</p>}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal className="max-w-[1100px] mx-auto mt-12">
            <button type="button"
                    data-testid="choir-riders-enlarge"
                    onClick={() => setChoirArt({ src: "/assets/art/choir-fleet-riders.jpg", label: "THE CHOIR", sub: "Zee · Boom · Killa · Peaches · BrickCity · Camo" })}
                    aria-label="Enlarge the Choir motorcycle lineup"
                    className="block w-full cursor-pointer">
              <img
                src="/assets/art/choir-fleet-riders.jpg"
                alt="The Choir on their motorcycles beneath the Memphis bridge at night"
                loading="lazy"
                data-testid="choir-riders-img"
                className="block w-full h-auto aspect-[1672/941] object-cover"
                style={{ background: "var(--cb-bg-2)" }}
              />
            </button>
          </Reveal>
        </div>
        <ArtLightbox active={choirArt} onClose={() => setChoirArt(null)} testid="choir-lightbox" />
      </section>

      {/* ============ THE INITIATIVE VS THE CHOIR ============ */}
      <section id="initiative-vs-choir" data-testid="section-vs"
               className="py-24 border-t border-[var(--cb-divider)]"
               style={{ background: "var(--cb-bg)" }}>
        <div className={SECTION_WRAP}>
          <SectionEyebrow>THE INITIATIVE VS THE CHOIR</SectionEyebrow>
          <h2 className="font-headline text-[var(--cb-text)] text-[30px] md:text-[44px] uppercase tracking-[0.04em] text-center mt-5 leading-tight">
            Two Families.<br /><span className="text-[var(--cb-gold)]">One City.</span>
          </h2>
          <Rule />
          <div className="grid md:grid-cols-2 gap-px bg-white/5 mt-10 overflow-hidden">
            {/* LEFT — INITIATIVE */}
            <Reveal>
              <div className="p-8 md:p-10 h-full"
                   style={{ background: "linear-gradient(180deg, #110D04 0%, #0A0A0A 100%)" }}>
                <div className="font-caps-wide text-[11px] text-[var(--cb-gold)]">THE INITIATIVE</div>
                <div className="h-px w-10 bg-[var(--cb-gold)] mt-4" />
                <p className="font-serif text-[15.5px] text-[var(--cb-text)] leading-[1.75] mt-5">
                  Built around second chances, responsibility, and service. The Initiative exists to protect life, preserve truth, and help people rebuild what was lost.
                </p>
                <div className="grid grid-cols-2 gap-2 mt-6">
                  {["Truth", "Redemption", "Discipline", "Service", "Protection"].map((v) => (
                    <div key={v} className="font-caps text-[10px] text-[var(--cb-gold)] py-1.5 border-l border-[var(--cb-gold)]/40 pl-3">{v}</div>
                  ))}
                </div>
                <div className="font-caps text-[10px] text-[var(--cb-text-dim)] mt-7">MEMBERS</div>
                <ul className="mt-3 space-y-1.5 font-serif text-[14.5px] text-[var(--cb-text)]">
                  {["Chase Michaels", "Pastor Wei Chen", "Andy Reyes", "Lillian Paige", "Noah Brown", "TRINITY", "The Overlook"].map((m) => (
                    <li key={m}>· {m}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
            {/* RIGHT — CHOIR */}
            <Reveal delay={0.12}>
              <div className="p-8 md:p-10 h-full"
                   style={{ background: "linear-gradient(180deg, #160C18 0%, #0A0A0A 100%)" }}>
                <div className="font-caps-wide text-[11px]" style={{ color: '#A06AB8' }}>THE CHOIR</div>
                <div className="h-px w-10 mt-4" style={{ background: '#A06AB8' }} />
                <p className="font-serif text-[15.5px] text-[var(--cb-text)] leading-[1.75] mt-5">
                  Built from people society abandoned. The Choir believes family comes before everything. Their loyalty is admirable. Their methods are dangerous.
                </p>
                <div className="grid grid-cols-2 gap-2 mt-6">
                  {["Loyalty", "Survival", "Belonging", "Power", "Protection"].map((v) => (
                    <div key={v} className="font-caps text-[10px] py-1.5 border-l pl-3" style={{ color: '#A06AB8', borderColor: 'rgba(160,106,184,0.4)' }}>{v}</div>
                  ))}
                </div>
                <div className="font-caps text-[10px] text-[var(--cb-text-dim)] mt-7">MEMBERS</div>
                <ul className="mt-3 space-y-1.5 font-serif text-[14.5px] text-[var(--cb-text)]">
                  {["Monk", "Zee", "Peaches", "Killa", "Boom", "BrickCity", "Camo", "The Choirhouse"].map((m) => (
                    <li key={m}>· {m}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ ELIJAH ALMER ============ */}
      <section id="elijah-almer" data-testid="section-almer"
               className="py-24 border-t border-[var(--cb-divider)]"
               style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(184,138,44,0.10) 0%, transparent 60%), var(--cb-bg)" }}>
        <div className={SECTION_WRAP}>
          <SectionEyebrow>ELIJAH ALMER</SectionEyebrow>
          <h2 className="font-headline text-[var(--cb-text)] text-[30px] md:text-[44px] uppercase tracking-[0.04em] text-center mt-5">
            The Man Who Started It All
          </h2>
          <Rule />
          <div className="grid md:grid-cols-12 gap-8 mt-10 items-start">
            <Reveal className="md:col-span-5 space-y-5">
              <div className="cb-card-static overflow-hidden">
                <img
                  src="/assets/art/almer-profile.jpg"
                  alt="Elijah Almer — profile portrait with his handwritten note to Cam"
                  loading="lazy"
                  data-testid="almer-profile-img"
                  className="block w-full h-auto"
                />
              </div>
              <div className="cb-card-static overflow-hidden p-3" style={{ background: "#14110B" }}>
                <img
                  src="/assets/art/almer-polaroid.jpg"
                  alt="First Day at Almer's Market — young Cam with Elijah Almer"
                  loading="lazy"
                  data-testid="almer-polaroid-img"
                  className="block w-full h-auto"
                />
                <div className="font-caps text-[9px] text-[var(--cb-text-dim)] text-center py-3">A LIFELONG BOND · ALMER'S MARKET</div>
              </div>
            </Reveal>
            <Reveal className="md:col-span-7 space-y-5" delay={0.12}>
              <p className={BODY_PROSE}>Elijah Almer knew Cam his entire life. Long before Cam became Cherubim, there was the man who never stopped believing in him.</p>
              <div className="cb-card-static overflow-hidden"
                   style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.45), 0 0 0 1px rgba(184,138,44,0.18)" }}>
                <video
                  data-testid="almer-legacy-video"
                  autoPlay muted loop playsInline preload="auto"
                  disablePictureInPicture
                  poster="/assets/video/almer-legacy-poster.jpg"
                  aria-label="Elijah Almer legacy video playing automatically"
                  className="pointer-events-none block w-full aspect-video bg-black"
                >
                  <source src="/assets/video/almer-legacy-loop.webm" type="video/webm" />
                  <source src="/assets/video/almer-legacy-loop.mp4" type="video/mp4" />
                </video>
              </div>
              <div className="flex flex-wrap gap-2">
                {["Business Owner", "Mentor", "Community Builder", "Father Figure"].map((t) => (
                  <div key={t} className="font-caps text-[10px] text-[var(--cb-gold)] border border-[var(--cb-gold)]/40 px-3 py-1.5">{t}</div>
                ))}
              </div>
              <p className={BODY_PROSE}>When the world gave up on Cam, Elijah did not. His belief becomes one of the forces that brings Cam home.</p>
              <p className="font-headline text-[var(--cb-gold)] text-[18px] md:text-[22px] uppercase tracking-[0.04em]">Through his legacy, Cam inherits more than property. He inherits responsibility.</p>
              <div className="grid grid-cols-2 gap-3 pt-2">
                {[["The Overlook", "Sanctuary · Command"], ["Almer's Market", "Surface · Storefront"], ["Resources", "Means"], ["Purpose", "Mission"]].map(([h, s]) => (
                  <div key={h} className="cb-card-static p-4">
                    <div className="font-caps text-[10px] text-[var(--cb-gold)]">{h}</div>
                    <div className="font-caps text-[9px] text-[var(--cb-text-dim)] mt-1.5">{s}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <InheritanceGallery />
        </div>
      </section>

      {/* ============ THE OVERLOOK (dedicated) ============ */}
      <section id="the-overlook" data-testid="section-overlook"
               className="py-24 border-t border-[var(--cb-divider)]">
        <div className={SECTION_WRAP}>
          <SectionEyebrow>THE OVERLOOK</SectionEyebrow>
          <h2 className="font-headline text-[var(--cb-text)] text-[30px] md:text-[44px] uppercase tracking-[0.04em] text-center mt-5">
            The Heart Of The Initiative
          </h2>
          <Rule />
          <Reveal className="max-w-[1100px] mx-auto mt-10">
            <img
              src="/assets/art/overlook-exterior.jpg"
              alt="The Overlook — the estate above Memphis at sunset"
              loading="lazy"
              data-testid="overlook-hero-img"
              className="block w-full h-auto"
            />
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-4 max-w-[1100px] mx-auto mt-5">
            {[
              ["/assets/art/overlook-library.jpg", "The Library — History In Context", "SANCTUARY"],
              ["/assets/art/overlook-watchroom.jpg", "The Watch Room — A Broader Perspective", "OPERATIONS"],
              ["/assets/art/overlook-journal.jpg", "Almer's Journal — Questions That Remain", "INHERITANCE"],
            ].map(([src, alt, tag], i) => (
              <Reveal key={alt} delay={i * 0.1}>
                <div className="cb-card-static overflow-hidden group">
                  <div className="overflow-hidden">
                    <img
                      src={src} alt={alt} loading="lazy"
                      data-testid={`overlook-interior-${tag.toLowerCase()}`}
                      className="block w-full h-auto transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="font-caps text-[9px] text-[var(--cb-gold)] text-center py-3">{tag}</div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="max-w-[720px] mx-auto mt-10 space-y-5 text-center">
            <p className={BODY_PROSE}>Perched above Memphis, The Overlook serves as the operational headquarters of the Initiative.</p>
            <div className="grid grid-cols-3 gap-3 max-w-md mx-auto py-3">
              {["Sanctuary", "Command", "Inheritance"].map((l) => (
                <div key={l} className="font-caps text-[10px] text-[var(--cb-gold)] border border-[var(--cb-gold)]/40 px-2 py-2">{l}</div>
              ))}
            </div>
            <p className={BODY_PROSE}>What begins as a gift from Elijah Almer becomes a place of refuge, command, and responsibility.</p>
            <p className="font-serif italic text-[var(--cb-gold)] text-[19px] leading-snug">
              Every major decision begins here.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============ THE WORLD ============ */}
      <section id="world" data-testid="section-world" className="py-24 border-t border-[var(--cb-divider)]">
        <div className={SECTION_WRAP}>
          <SectionEyebrow>THE WORLD</SectionEyebrow>
          <Rule />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {WORLD.map((w, i) => (
              <Reveal key={w.name} delay={(i % 3) * 0.08}>
                <article
                         data-testid={`world-card-${w.name.replace(/[^A-Za-z]/g, '-').toLowerCase()}`}
                         className="cb-card-static flex flex-col overflow-hidden h-full">
                  <header className="px-6 pt-7 text-center">
                    <h3 className="font-headline text-[var(--cb-gold)] text-[22px] md:text-[24px] tracking-[0.03em] uppercase">{w.name}</h3>
                    <div className="h-px w-10 bg-[var(--cb-gold)] mt-4 mx-auto" />
                  </header>
                  {w.image ? (
                    <div className="mt-6 w-full overflow-hidden aspect-[4/3] sm:aspect-[16/10] bg-[var(--cb-bg-3)]">
                      <button type="button"
                              data-testid={`world-enlarge-${w.name.replace(/[^A-Za-z]/g, '-').toLowerCase()}`}
                              onClick={() => setChoirArt({ src: w.image, label: "THE WORLD", sub: w.name })}
                              aria-label={`Enlarge ${w.name} artwork`}
                              className="block w-full h-full cursor-pointer">
                        <img src={w.image} alt={w.name} loading="lazy"
                             style={w.imagePosition ? { objectPosition: w.imagePosition } : undefined}
                             className={`block w-full h-full ${w.fit === "contain" ? "object-contain" : "object-cover"}`} />
                      </button>
                    </div>
                  ) : (
                    <div className="mt-6 w-full aspect-[16/10] relative overflow-hidden">
                      <div className="absolute inset-0" style={{
                        background: "radial-gradient(ellipse at 60% 50%, rgba(184,138,44,0.20) 0%, transparent 60%), linear-gradient(180deg, #11101A 0%, #0A0A0A 100%)"
                      }} />
                      <div className="filigree absolute inset-[18%] opacity-40" />
                      <div className="absolute inset-0 flex items-center justify-center font-caps text-[10px] text-[var(--cb-text-muted)]">KEY ART · FORTHCOMING</div>
                    </div>
                  )}
                  <div className="px-6 mt-6 pb-7">
                    <p className="font-serif text-[15.5px] text-[var(--cb-text)] leading-[1.75] text-left">{w.body}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ THE CHERUBIM PLATFORM ============ */}
      <section id="cherubim-platform" data-testid="section-platform"
               className="py-24 border-t border-[var(--cb-divider)]"
               style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(184,138,44,0.14) 0%, transparent 60%), linear-gradient(180deg, var(--cb-bg) 0%, #0E0B14 100%)" }}>
        <div className={SECTION_WRAP}>
          <SectionEyebrow>CHERUBIM</SectionEyebrow>
          <h2 className="font-headline text-[var(--cb-text)] text-[30px] md:text-[44px] uppercase tracking-[0.04em] text-center mt-5">
            The Guardian System
          </h2>
          <Rule />
          <Reveal className="max-w-[760px] mx-auto mt-10 space-y-5 text-center">
            <p className={BODY_PROSE}>The Cherubim platform was never designed to create a conqueror. It was designed to support a guardian.</p>
            <p className={BODY_PROSE}>The technology expands what Cam can do. Faith determines what he chooses to do with it.</p>
            <div className="pt-3 space-y-2">
              <p className="font-headline text-[var(--cb-text)] text-[18px] md:text-[22px] uppercase tracking-[0.04em]">Power alone is not enough.</p>
              <p className="font-headline text-[var(--cb-gold)] text-[18px] md:text-[22px] uppercase tracking-[0.04em]">The mission matters. The person matters. The choice matters.</p>
            </div>
          </Reveal>
          <Reveal className="mt-10 cb-card-static overflow-hidden max-w-[980px] mx-auto" delay={0.1}>
            <div className="grid md:grid-cols-2 items-stretch">
              <div className="overflow-hidden min-h-[280px]">
                <img
                  src="/assets/art/etching.jpg"
                  alt="The Etching — scripture-light tracing across Cam's body as armor materializes"
                  loading="lazy"
                  data-testid="etching-img"
                  className="block w-full h-full object-cover"
                />
              </div>
              <div className="p-8 md:p-10 flex flex-col justify-center text-center md:text-left">
                <div className="font-caps-wide text-[11px] text-[var(--cb-gold)]">THE ETCHING</div>
                <div className="h-px w-8 bg-[var(--cb-gold)] my-4 mx-auto md:mx-0" />
                <p className="font-headline text-[var(--cb-text)] text-[18px] md:text-[20px] uppercase tracking-[0.03em] leading-snug">
                  Scripture forms the armor.
                </p>
                <p className="font-serif italic text-[var(--cb-text-dim)] text-[15px] md:text-[16px] leading-relaxed mt-4">
                  Scripture geometry materializing in light across the armor — the visible signature of the Guardian System.
                </p>
              </div>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-12">
            {[
              ["Protective Armor", "Built to shield"],
              ["TRINITY", "Mission intelligence"],
              ["Human Choice", "Cam still chooses"],
              ["Mobility", "Built for response"],
              ["Protection", "Civilian-first"],
              ["Restraint", "Mercy by design"],
            ].map(([h, s], i) => (
              <Reveal key={h} delay={(i % 3) * 0.08}>
                <div className="cb-card p-5 h-full">
                  <div className="font-caps text-[10px] text-[var(--cb-gold)]">{h}</div>
                  <div className="h-px w-8 bg-[var(--cb-gold)] my-3" />
                  <div className="font-caps text-[9px] text-[var(--cb-text-dim)]">{s}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ THE DIFFERENTIATOR — THE DAMPENING ============ */}
      <DampeningSection />

      {/* ============ THE TECHNOLOGY ============ */}
      <TechnologySection />

      {/* ============ THE THREE FROM THE FURNACE ============ */}
      <ThreeFromFurnaceSection />

      {/* ============ FRANCHISE STRENGTHS ============ */}
      <section id="franchise-strengths" data-testid="section-strengths"
               className="py-24 border-t border-[var(--cb-divider)]"
               style={{ background: "var(--cb-bg)" }}>
        <div className={SECTION_WRAP}>
          <SectionEyebrow>FRANCHISE STRENGTHS</SectionEyebrow>
          <h2 className="font-headline text-[var(--cb-text)] text-[30px] md:text-[44px] uppercase tracking-[0.04em] text-center mt-5 leading-tight">
            Built For<br /><span className="text-[var(--cb-gold)]">Long-Form Storytelling</span>
          </h2>
          <Rule />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {[
              ["4", "Season Series Roadmap"],
              ["+", "Feature Film Expansion"],
              ["NEW", "Faith-Tech Genre"],
              ["NOW", "Contemporary Themes"],
            ].map(([n, l], i) => (
              <Reveal key={l} delay={i * 0.08}>
                <div className="cb-card p-6 text-center h-full">
                  <div className="font-headline text-[var(--cb-gold)] text-[36px] md:text-[44px] leading-none">{n}</div>
                  <div className="font-caps text-[10px] text-[var(--cb-text-dim)] mt-4">{l}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ THE FRANCHISE ============ */}
      <FranchiseSection />
      <VisualArchive />

      {/* ============ EXECUTIVE ACCESS ============ */}
      <ExecutiveAccessSection />

      {/* ============ THE CREATOR ============ */}
      <section id="the-creator" data-testid="section-creator" className="py-24 border-t border-[var(--cb-divider)]">
        <Reveal className={SECTION_WRAP}>
          <SectionEyebrow>THE CREATOR</SectionEyebrow>
          <Rule />
          <div className="max-w-[720px] mx-auto text-center mt-10 space-y-6">
            <h2 className="font-headline text-[var(--cb-text)] text-[28px] md:text-[40px] uppercase tracking-[0.04em]">
              Ashton D. Brown Jr.
            </h2>
            <div className="font-caps text-[11px] text-[var(--cb-gold)]">
              CREATOR · WRITER · WORLD BUILDER · FOUNDER OF ASHECRAFT INTERNATIONAL
            </div>
            <p className={BODY_PROSE}>
              Building stories where redemption, responsibility, truth, and sacrifice matter.
            </p>
          </div>
        </Reveal>
      </section>

      {/* ============ COMMUNITY ============ */}
      <CommunitySection />

      {/* ============ FINAL CTA ============ */}
      <FinalCtaSection />

      {/* ============ CONTACT ============ */}
      <ContactSection />

      {/* ============ TEASER MODAL ============ */}
      <TeaserModal open={teaserOpen} onClose={() => setTeaserOpen(false)} />
    </div>
  );
};

/* ---------- TEASER MODAL (sound-on cinematic player) ---------- */
const TeaserModal = ({ open, onClose }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div data-testid="teaser-modal"
         className="fixed inset-0 z-[130] flex items-center justify-center p-4"
         style={{ background: "rgba(4,4,7,0.96)", backdropFilter: "blur(10px)" }}
         onClick={onClose}>
      <button type="button"
              data-testid="teaser-close"
              aria-label="Close teaser"
              onClick={onClose}
              className="absolute top-5 right-5 text-white/75 hover:text-[var(--cb-gold)] transition-colors duration-300 cursor-pointer z-10">
        <X size={26} strokeWidth={1.5} />
      </button>
      <div className="w-full max-w-[1100px]" onClick={(e) => e.stopPropagation()}>
        {failed ? (
          <div className="text-center py-20" data-testid="teaser-fallback">
            <div className="cb-logo text-[24px]" style={{ letterSpacing: '0.1em' }}>TEASER ARRIVING SHORTLY</div>
            <p className="font-serif italic text-[var(--cb-text-dim)] text-[14px] mt-4">The official teaser is being prepared.</p>
          </div>
        ) : (
          <video
            poster="/assets/video/teaser-poster.jpg"
            controls autoPlay playsInline
            onError={() => setFailed(true)}
            data-testid="teaser-video"
            className="w-full h-auto max-h-[80vh] bg-black"
            style={{ boxShadow: "0 24px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(184,138,44,0.35)" }}
          >
            <source src="/assets/video/teaser.webm" type="video/webm" />
            <source src="/assets/video/teaser.mp4" type="video/mp4" />
          </video>
        )}
        <div className="font-caps-wide text-[9px] text-[var(--cb-gold)] text-center mt-4">CHERUBIM · OFFICIAL TEASER</div>
      </div>
    </div>
  );
};

/* =========================================================== */
/* ============ THE DIFFERENTIATOR — THE DAMPENING ============ */
/* =========================================================== */
const DampeningSection = () => (
  <section id="the-dampening" data-testid="section-dampening"
           className="py-16 md:py-20 border-t border-[var(--cb-divider)]"
           style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(74,31,92,0.20) 0%, transparent 60%), var(--cb-bg)" }}>
    <div className={SECTION_WRAP}>
      <Reveal className="max-w-[760px] mx-auto text-center">
        <SectionEyebrow>THE DIFFERENTIATOR</SectionEyebrow>
        <h2 className="font-headline text-[var(--cb-text)] text-[30px] md:text-[44px] uppercase tracking-[0.04em] mt-5 leading-tight">
          The <span className="text-[var(--cb-gold)]">Dampening</span>
        </h2>
        <p className="font-serif italic text-[var(--cb-text-dim)] text-[19px] md:text-[22px] leading-[1.65] mt-7">
          <span className="block">The power is always his.</span>
          <span className="block text-[var(--cb-gold)] mt-2">But when vengeance overtakes truth, the system begins to resist him.</span>
        </p>

        <div className="cb-card overflow-hidden mt-8">
          <video
            data-testid="dampening-loop-video"
            autoPlay muted loop playsInline preload="auto"
            disablePictureInPicture
            poster="/assets/video/dampening-loop-poster.jpg"
            aria-label="The Dampening motion loop playing automatically"
            className="pointer-events-none block w-full h-auto bg-black"
          >
            <source src="/assets/video/dampening-loop.webm" type="video/webm" />
            <source src="/assets/video/dampening-loop.mp4" type="video/mp4" />
          </video>
        </div>

        <a href="#executive-access" data-testid="dampening-executive-cta"
           className="btn-outline-cb inline-flex items-center gap-2.5 mt-9">
          REQUEST THE EXECUTIVE BRIEFING <ArrowRight size={14} />
        </a>
      </Reveal>
    </div>
  </section>
);

/* =========================================================== */
/* ============ THE TECHNOLOGY — SYSTEMS ===================== */
/* =========================================================== */
const TechnologySection = () => {
  return (
    <section id="technology" data-testid="section-technology"
             className="py-24 border-t border-[var(--cb-divider)]"
             style={{ background: "linear-gradient(180deg, var(--cb-bg) 0%, #0E0B14 100%)" }}>
      <div className={SECTION_WRAP}>
        <SectionEyebrow>THE TECHNOLOGY</SectionEyebrow>
        <Rule />

        {/* SYSTEMS — the platform and the intelligence */}
        <div className="grid sm:grid-cols-2 gap-4 mt-10">
          {SYSTEMS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1}>
              <article data-testid={`tech-card-${t.name.toLowerCase()}`}
                       className="cb-card flex flex-col overflow-hidden h-full group">
                <div className="relative w-full aspect-[16/10] overflow-hidden bg-[var(--cb-bg-3)]">
                  <img src={t.image} alt={t.name} loading="lazy"
                       className="block w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]" />
                </div>
                <div className="p-6">
                  <h3 className="font-headline text-[var(--cb-gold)] text-[18px] md:text-[20px] uppercase tracking-[0.03em]">{t.name}</h3>
                  <div className="font-caps text-[10px] text-[var(--cb-text-dim)] mt-2">{t.sub}</div>
                  <p className="font-serif text-[14.5px] text-[var(--cb-text)] leading-[1.7] mt-4">{t.body}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

      </div>
    </section>
  );
};

/* =========================================================== */
/* ============ THE THREE FROM THE FURNACE =================== */
/* =========================================================== */
const ThreeFromFurnaceSection = () => (
  <section id="the-three-from-the-furnace" data-testid="section-three-furnace"
           className="py-20 md:py-24 border-t border-[var(--cb-divider)]"
           style={{ background: "linear-gradient(180deg, #0E0B14 0%, var(--cb-bg) 100%)" }}>
    <div className={SECTION_WRAP}>
      <Reveal className="max-w-[980px] mx-auto text-center">
        <h2 className="font-headline text-[var(--cb-text)] text-[30px] md:text-[44px] uppercase tracking-[0.04em] leading-tight">
          The Three From <span className="text-[var(--cb-gold)]">The Furnace</span>
        </h2>
        <div className="h-px w-14 bg-[var(--cb-gold)] mt-6 mx-auto" />
        <div className="cb-card overflow-hidden mt-10">
          <img
            src="/assets/art/fleet-banner.jpg"
            alt="The Three from the Furnace — Shadrach, Meshach, and Abednego in The Overlook garage"
            loading="lazy"
            data-testid="furnace-hero-img"
            className="block w-full h-auto"
          />
        </div>
        <p data-testid="furnace-description"
           className="font-serif italic text-[var(--cb-text-dim)] text-[17px] md:text-[19px] leading-[1.7] mt-7 max-w-[720px] mx-auto">
          Cam’s inherited fleet — Shadrach, Meshach, and Abednego. Built for different roads. Bound by the same legacy.
        </p>
        <Link to="/vehicles" data-testid="cta-explore-vehicles"
              className="btn-outline-cb inline-flex items-center gap-2.5 mt-8">
          EXPLORE THE VEHICLES <ArrowRight size={14} />
        </Link>
      </Reveal>
    </div>
  </section>
);

/* =========================================================== */
/* ============ ESTATE ARCHIVE (dossier library) ============= */
/* =========================================================== */
const EstateArchive = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    const savedY = window.scrollY;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      requestAnimationFrame(() => {
        if (window.__lenis) window.__lenis.scrollTo(savedY, { immediate: true, force: true });
        else window.scrollTo({ top: savedY, behavior: "instant" });
      });
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div data-testid="estate-archive" className="fixed inset-0 z-[125] overflow-y-auto"
         style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(184,138,44,0.07) 0%, transparent 45%), #060608" }}>
      <button type="button"
              data-testid="archive-close"
              aria-label="Close estate archive"
              onClick={onClose}
              className="fixed top-5 right-5 z-10 w-11 h-11 flex items-center justify-center border border-[var(--cb-divider)] text-white/75 hover:text-[var(--cb-gold)] hover:border-[var(--cb-gold)] transition-colors duration-300 cursor-pointer"
              style={{ background: "rgba(10,10,12,0.9)", backdropFilter: "blur(12px)" }}>
        <X size={20} strokeWidth={1.5} />
      </button>

      <div className="max-w-[1100px] mx-auto px-6 py-16 md:py-20">
        <div className="text-center">
          <div className="font-caps-wide text-[10px] text-[var(--cb-gold)]">ESTATE OF ELIJAH ALMER</div>
          <h3 className="font-headline text-[var(--cb-text)] text-[26px] md:text-[38px] uppercase tracking-[0.04em] mt-4">The Complete Estate Archive</h3>
          <p className="font-serif italic text-[var(--cb-text-dim)] text-[15px] mt-4">
            Exhibits · Deeds · Maps · Letters · Estate Records
          </p>
          <div className="h-px w-16 bg-[var(--cb-gold)] mt-6 mx-auto" />
          <div className="font-caps text-[9px] text-[var(--cb-text-muted)] mt-5">
            EACH DOCUMENT OPENS IN A SEPARATE WINDOW AT FULL READABLE SIZE
          </div>
        </div>

        {ARCHIVE_GROUPS.map((g) => (
          <div key={g.group} className="mt-14" data-testid={`archive-group-${g.group.replace(/[^A-Za-z]/g, '-').toLowerCase()}`}>
            <div className="flex items-center gap-4">
              <span className="font-caps text-[10px] text-[var(--cb-gold)] whitespace-nowrap">{g.group}</span>
              <span className="h-px flex-1 bg-[var(--cb-divider)]" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-6">
              {g.docs.map((d) => (
                <div key={d.key}
                     data-testid={`archive-doc-${d.key}`}
                     className="cb-card-static overflow-hidden flex flex-col">
                  <div className="aspect-[3/4] overflow-hidden bg-[var(--cb-bg-3)]">
                    <img src={DOC_URL(d.key)} alt={d.title} loading="lazy"
                         className="block w-full h-full object-cover object-top" />
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <div className="font-caps text-[9px] text-[var(--cb-text-muted)]">{d.type.toUpperCase()}</div>
                    <div className="font-headline text-[var(--cb-text)] text-[13px] uppercase tracking-[0.03em] mt-2 leading-snug flex-1">{d.title}</div>
                    <a href={DOC_URL(d.key)} target="_blank" rel="noopener noreferrer"
                       data-testid={`doc-view-${d.key}`}
                       className="mt-4 block text-center font-caps text-[10px] py-2.5 px-3 border border-[var(--cb-gold)] text-[var(--cb-gold)] hover:bg-[var(--cb-gold)] hover:text-black transition-colors duration-300 cursor-pointer">
                      View Document
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="text-center mt-16">
          <button type="button" onClick={onClose} data-testid="archive-return"
                  className="btn-primary-cb">Return To The Estate</button>
          <div className="font-caps text-[9px] text-[var(--cb-text-muted)] mt-6">
            CONSOLIDATED PACKAGE AVAILABLE VIA THE EXECUTIVE BRIEFING CENTER
          </div>
        </div>
      </div>
    </div>
  );
};

/* =========================================================== */
/* ============ INHERITANCE DOCUMENTS GALLERY ================ */
/* =========================================================== */
const InheritanceGallery = () => {
  const [archiveOpen, setArchiveOpen] = useState(false);
  const totalDocs = ARCHIVE_GROUPS.reduce((n, g) => n + g.docs.length, 0);

  return (
    <div className="mt-16" data-testid="inheritance-gallery">
      <div className="font-caps-wide text-[11px] text-[var(--cb-gold)] text-center">INHERITANCE DOCUMENTS</div>
      <h3 className="font-headline text-[var(--cb-text)] text-[22px] md:text-[28px] uppercase tracking-[0.03em] text-center mt-3">Estate of Elijah Almer</h3>
      <div className="h-px w-12 bg-[var(--cb-gold)] mt-4 mx-auto" />
      <p className="font-serif italic text-[var(--cb-text-dim)] text-center mt-5 text-[16px] max-w-[720px] mx-auto">
        Exhibits, deeds, maps, and letters. One legacy. What Chase Alexander Michaels inherits.
      </p>

      <div className="max-w-[420px] mx-auto mt-10">
        {[
          { key: "exhibit-a", title: "Real Estate Holdings", type: "Exhibit A" },
        ].map((d) => (
          <Reveal key={d.key}>
            <a href={DOC_URL(d.key)} target="_blank" rel="noopener noreferrer"
               data-testid={`estate-preview-${d.key}`}
               className="cb-card-static group block overflow-hidden cursor-pointer transition-transform duration-500 hover:-translate-y-1">
              <div className="overflow-hidden bg-[var(--cb-bg-3)]">
                <img src={DOC_URL(d.key)} alt={d.title} loading="lazy"
                     className="block w-full h-auto transition-transform duration-[1200ms] ease-out group-hover:scale-[1.02]" />
              </div>
              <div className="p-5 text-center border-t border-[var(--cb-divider)]">
                <div className="font-caps text-[9px] text-[var(--cb-gold)]">{d.type.toUpperCase()}</div>
                <div className="font-headline text-[var(--cb-text)] text-[14px] uppercase tracking-[0.04em] mt-2">{d.title}</div>
              </div>
            </a>
          </Reveal>
        ))}
      </div>

      <Reveal className="max-w-[640px] mx-auto mt-8">
        <button
          type="button"
          data-testid="archive-open-button"
          onClick={() => setArchiveOpen(true)}
          className="w-full cb-card-static group cursor-pointer relative overflow-hidden px-8 py-10 text-center transition-transform duration-500 hover:-translate-y-1"
        >
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
               style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(184,138,44,0.10) 0%, transparent 60%)" }} />
          <FileLock2 size={20} className="mx-auto text-[var(--cb-gold)]" />
          <div className="font-headline text-[var(--cb-text)] text-[16px] md:text-[19px] uppercase tracking-[0.06em] mt-5 leading-snug">
            View The Complete<br />Estate Archive
          </div>
          <div className="font-caps text-[9px] text-[var(--cb-text-muted)] mt-4">
            EXHIBITS • DEEDS • MAPS • LETTERS • ESTATE RECORDS
          </div>
          <div className="font-caps text-[9px] text-[var(--cb-gold)] mt-5 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
            {totalDocs} DOCUMENTS · ENTER THE DOSSIER →
          </div>
        </button>
      </Reveal>

      <EstateArchive open={archiveOpen} onClose={() => setArchiveOpen(false)} />
    </div>
  );
};

/* =========================================================== */
/* ============ THE CHERUBIM INITIATIVE (access list) ======== */
/* =========================================================== */
const InitiativeSignupForm = ({ source, primary = false, testidPrefix = "init" }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/subscribe", { email, source });
      toast.success("You're on the list. Welcome to The Initiative.");
      setDone(true);
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid={`${testidPrefix}-submitted`} className="font-caps text-[11px] text-[var(--cb-gold)] text-center py-4">
        ACCESS GRANTED · CHECK YOUR INBOX FOR THE FIRST TRANSMISSION
      </div>
    );
  }
  return (
    <form data-testid={`${testidPrefix}-form`} onSubmit={submit} className="flex flex-col sm:flex-row gap-3 mt-2">
      <input
        data-testid={`${testidPrefix}-email`}
        type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="your.email@studio.com"
        className="cb-input flex-1"
      />
      <button
        data-testid={`${testidPrefix}-submit`}
        type="submit" disabled={loading}
        className={primary ? "btn-primary-cb whitespace-nowrap" : "btn-outline-cb whitespace-nowrap"}
      >
        {loading ? "ENTERING…" : "ENTER THE UNIVERSE"} <ArrowRight size={13} />
      </button>
    </form>
  );
};

const CherubimInitiativeSection = () => (
  <section id="cherubim-initiative" data-testid="section-initiative-signup"
           className="py-24 border-t border-[var(--cb-divider)]"
           style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(184,138,44,0.16) 0%, transparent 55%), radial-gradient(ellipse at 80% 100%, rgba(74,31,92,0.18) 0%, transparent 55%), var(--cb-bg)" }}>
    <Reveal className={SECTION_WRAP}>
      <SectionEyebrow>THE CHERUBIM INITIATIVE</SectionEyebrow>
      <h2 className="font-headline text-[var(--cb-text)] text-[28px] md:text-[40px] uppercase tracking-[0.04em] text-center mt-5 leading-tight">
        Become one of the first to enter the world of <span className="text-[var(--cb-gold)]">CHERUBIM</span>.
      </h2>
      <Rule />
      <div className="max-w-[720px] mx-auto mt-10 space-y-4">
        <p className={`${BODY_PROSE} text-center`}>
          The world believes Chase “Cam” Michaels disappeared. <span className="text-[var(--cb-gold)]">They were wrong.</span>
        </p>
        <p className={`${BODY_PROSE} text-center`}>
          Sentenced to life without parole for a crime he didn’t commit, decorated soldier Chase “Cam” Michaels receives an extraordinary release — and discovers his name was buried for a reason.
        </p>
        <div className="text-center space-y-2 pt-2">
          <p className="font-headline text-[var(--cb-text)] text-[16px] md:text-[18px] uppercase tracking-[0.04em]">Armed with advanced technology.</p>
          <p className="font-headline text-[var(--cb-text)] text-[16px] md:text-[18px] uppercase tracking-[0.04em]">Guided by Scripture.</p>
          <p className="font-headline text-[var(--cb-gold)] text-[18px] md:text-[22px] uppercase tracking-[0.04em]">He becomes CHERUBIM.</p>
        </div>
      </div>

      <div className="max-w-[720px] mx-auto mt-12 cb-card p-7 md:p-9">
        <div className="font-caps text-[11px] text-[var(--cb-gold)] text-center">JOIN THE OFFICIAL ACCESS LIST</div>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-6 font-serif text-[14.5px] text-[var(--cb-text)]">
          {[
            "Exclusive teaser drops",
            "Character reveals",
            "Behind-the-scenes",
            "Production updates",
            "Merchandise announcements",
            "Early episode announcements",
            "First-look concept art",
            "Priority screening invitations",
          ].map((b) => <div key={b}>· {b}</div>)}
        </div>
        <div className="mt-7">
          <InitiativeSignupForm source="homepage_initiative" primary testidPrefix="init" />
        </div>
        <div className="font-caps text-[9px] text-[var(--cb-text-muted)] text-center mt-5">
          NO SPAM. CINEMATIC TRANSMISSIONS ONLY.
        </div>
      </div>
    </Reveal>
  </section>
);

const SeriesVideoSection = () => (
  <section id="series-preview" data-testid="section-series-preview"
           className="relative py-4 md:py-6"
           style={{ background: "linear-gradient(180deg, var(--cb-bg) 0%, #07070A 100%)" }}>
    <Reveal className={SECTION_WRAP}>
      <div className="max-w-[980px] mx-auto cb-card overflow-hidden"
           style={{ boxShadow: "0 24px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(184,138,44,0.22)" }}>
        <video
          data-testid="series-preview-video"
          autoPlay muted loop playsInline preload="auto"
          disablePictureInPicture
          poster="/assets/video/cherubim-preview-poster.jpg"
          aria-label="CHERUBIM preview video playing automatically"
          className="pointer-events-none block w-full aspect-video bg-black"
        >
          <source src="/assets/video/cherubim-preview.webm" type="video/webm" />
          <source src="/assets/video/cherubim-preview.mp4" type="video/mp4" />
        </video>
      </div>
    </Reveal>
  </section>
);

/* =========================================================== */
/* ===================== ENTER THE STORY ===================== */
/* =========================================================== */
const ENTER_LINES = [
  "They tried to erase his name.",
  "But Heaven remembers.",
  "Truth has a price.",
  "Justice has a witness.",
  "Faith has a warrior.",
];

const EnterTheStorySection = () => (
  <section id="enter-the-story" data-testid="section-enter"
           className="pt-28 pb-8 md:pb-12 border-t border-[var(--cb-divider)]"
           style={{ background: "radial-gradient(ellipse at 50% 50%, rgba(184,138,44,0.10) 0%, transparent 55%), #050505" }}>
    <div className={SECTION_WRAP}>
      <SectionEyebrow>ENTER THE STORY</SectionEyebrow>
      <Rule />
      <Reveal className="max-w-[880px] mx-auto mt-10">
        <div data-testid="enter-title-mark"
             className="flex items-center justify-center py-16 md:py-24"
             style={{ background: "radial-gradient(ellipse at 50% 55%, rgba(184,138,44,0.10) 0%, transparent 55%), #050505", border: "1px solid var(--cb-divider)" }}>
          <img
            src="/assets/art/cherubim-logo-wide.png"
            alt="CHERUBIM"
            loading="lazy"
            className="h-auto w-[min(78vw,680px)]"
          />
        </div>
      </Reveal>
      <div className="max-w-[860px] mx-auto mt-14 text-center space-y-7">
        {ENTER_LINES.map((line, i) => (
          <Reveal key={line} delay={i * 0.14}>
            <p
              className="font-headline uppercase tracking-[0.04em] leading-[1.15]"
              style={{
                fontSize: 'clamp(20px, 4.4vw, 44px)',
                color: i < 2 ? 'var(--cb-text)' : 'var(--cb-gold)',
              }}
            >
              {i === 1 ? <>But <span className="text-[var(--cb-gold)]">Heaven remembers.</span></> : line}
            </p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* =========================================================== */
/* ======================= COMMUNITY ========================= */
/* =========================================================== */
const CommunitySection = () => (
  <section id="community" data-testid="section-community"
           className="py-24 border-t border-[var(--cb-divider)]"
           style={{ background: "var(--cb-bg)" }}>
    <Reveal className={SECTION_WRAP}>
      <SectionEyebrow>THE COMMUNITY</SectionEyebrow>
      <h2 className="font-headline text-[var(--cb-text)] text-[28px] md:text-[40px] uppercase tracking-[0.04em] text-center mt-5">
        Become A Witness.
      </h2>
      <p className="font-serif italic text-[var(--cb-text-dim)] text-center mt-4 text-[16px] max-w-[640px] mx-auto">
        This wall is reserved for authentic audience reactions as the world of CHERUBIM reveals itself. No fabricated quotes. No invented voices.
      </p>
      <Rule />
      <div className="max-w-[680px] mx-auto mt-12 cb-card p-7 md:p-9">
        <div className="font-caps text-[11px] text-[var(--cb-gold)] text-center">JOIN THE MOVEMENT</div>
        <p className="font-serif text-[15.5px] text-[var(--cb-text)] leading-[1.75] text-center mt-5">
          Stand with the first witnesses of the franchise. Real reactions from real audiences will live here — yours could be among them.
        </p>
        <div className="mt-7">
          <InitiativeSignupForm source="community" primary testidPrefix="community" />
        </div>
      </div>
    </Reveal>
  </section>
);

/* =========================================================== */
/* ======================= FINAL CTA ========================= */
/* =========================================================== */
const FinalCtaSection = () => (
  <section id="final-cta" data-testid="section-final-cta"
           className="py-28 border-t border-[var(--cb-divider)] relative overflow-hidden"
           style={{ background: "#050505" }}>
    <div className="absolute inset-0">
      <img
        src="/assets/art/final-cta-bg.jpg"
        alt="" aria-hidden="true"
        data-testid="final-cta-bg"
        className="block w-full h-full object-cover opacity-45"
        style={{ objectPosition: "center 30%" }}
      />
      <div className="absolute inset-0" style={{
        background: "radial-gradient(ellipse at 50% 45%, rgba(5,5,5,0.55) 0%, rgba(5,5,5,0.88) 75%), linear-gradient(180deg, rgba(5,5,5,0.85) 0%, rgba(5,5,5,0.35) 40%, rgba(5,5,5,0.9) 100%)"
      }} />
    </div>
    <div className="filigree absolute" style={{ inset: 'auto -10% -20% auto', width: 520, height: 520, opacity: 0.25 }} />
    <Reveal className={`relative z-10 ${SECTION_WRAP} text-center`}>
      <h2 className="font-headline uppercase text-[var(--cb-text)] leading-[1.05] tracking-[0.04em]"
          style={{ fontSize: 'clamp(28px, 6vw, 60px)' }}>
        They Tried To Erase His Name.
      </h2>
      <h2 className="font-headline uppercase text-[var(--cb-gold)] leading-[1.05] tracking-[0.04em] mt-3"
          style={{ fontSize: 'clamp(28px, 6vw, 60px)' }}>
        But Heaven Remembers.
      </h2>
      <p className="font-serif italic text-[var(--cb-text-dim)] text-[17px] md:text-[19px] mt-8 max-w-xl mx-auto">
        Join the mission before Episode One.
      </p>
      <div className="max-w-xl mx-auto mt-8">
        <InitiativeSignupForm source="final_cta" primary testidPrefix="finalcta" />
      </div>
    </Reveal>
  </section>
);

/* =========================================================== */
/* ============ THE FRANCHISE — TRINITY SAGA SLATE =========== */
/* =========================================================== */
const FranchiseSection = () => {
  const [activeArt, setActiveArt] = useState(null);

  return (
    <section id="franchise" data-testid="section-franchise" className="py-24 border-t border-[var(--cb-divider)]"
             style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(184,138,44,0.06) 0%, transparent 60%), var(--cb-bg)" }}>
      <div className={SECTION_WRAP}>
        <SectionEyebrow>THE FRANCHISE</SectionEyebrow>
        <div className="flex justify-center mt-8">
          <img src="/assets/art/cherubim-universe-logo.png" alt="Cherubim Universe emblem"
               data-testid="franchise-universe-logo"
               style={{ mixBlendMode: "screen" }}
               className="w-[240px] sm:w-[320px] md:w-[400px] h-auto" />
        </div>
        <h2 className="font-headline text-[var(--cb-text)] text-[32px] md:text-[44px] uppercase tracking-[0.04em] text-center mt-5">
          The Trinity Saga
        </h2>
        <p className="font-serif italic text-[var(--cb-text-dim)] text-center mt-4 text-[17px] max-w-[720px] mx-auto">
          One flagship. Multiple expansion paths.
        </p>
        <Rule />
        <div className="grid md:grid-cols-2 gap-5 mt-12">
          {FRANCHISE.map((p, i) => (
            <Reveal key={p.code} delay={(i % 2) * 0.1}>
              <article
                       data-testid={`franchise-card-${p.code.replace(/[^A-Za-z0-9]/g, '-').toLowerCase()}`}
                       className="cb-card flex flex-col overflow-hidden group transition-transform duration-500 hover:-translate-y-1 h-full">
                {p.image ? (
                  <button type="button"
                          data-testid={`franchise-enlarge-${p.code.replace(/[^A-Za-z0-9]/g, '-').toLowerCase()}`}
                          onClick={() => setActiveArt({ src: p.image, label: "CHERUBIM UNIVERSE", sub: p.full ? `${p.name} — ${p.full}` : p.name })}
                          aria-label={`Enlarge ${p.full || p.name} key art`}
                          className="relative block w-full aspect-video overflow-hidden cursor-pointer text-left">
                    <img src={p.image} alt={`${p.full || p.name} key art`} loading="lazy"
                         style={p.imagePosition ? { objectPosition: p.imagePosition } : undefined}
                         className={`absolute inset-0 w-full h-full ${p.fit === "contain" ? "object-contain" : "object-cover"} transition-transform duration-700 group-hover:scale-[1.03]`} />
                  </button>
                ) : (
                  <div className="relative w-full aspect-video overflow-hidden">
                    <div className="absolute inset-0" style={{
                      background: `radial-gradient(ellipse at 30% 35%, ${p.accent} 0%, transparent 55%), radial-gradient(ellipse at 80% 80%, ${p.tint} 0%, transparent 60%), linear-gradient(180deg, #0E0E12 0%, #0A0A0A 100%)`,
                    }} />
                    <div className="filigree absolute inset-[15%] opacity-50" />
                    <div className="absolute inset-0 hero-grain" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                      <div className="font-caps-wide text-[10px] sm:text-[11px] text-[var(--cb-gold)]">CHERUBIM UNIVERSE</div>
                      <div className="cb-logo mt-3 text-[22px] sm:text-[26px] md:text-[30px]" style={{ letterSpacing: '0.08em' }}>{p.code}</div>
                      <div className="font-caps text-[9px] text-[var(--cb-text-muted)] mt-3">KEY ART · FORTHCOMING</div>
                    </div>
                  </div>
                )}
                <div className="p-6 md:p-7 flex flex-col flex-1">
                  <h3 className="font-headline text-[var(--cb-gold)] text-[20px] md:text-[24px] uppercase tracking-[0.03em]">{p.name}</h3>
                  {p.full && (
                    <div className="font-caps text-[10px] tracking-[0.2em] text-[var(--cb-text)] mt-1.5">{p.full}</div>
                  )}
                  <div className="font-caps text-[10px] text-[var(--cb-text-dim)] mt-2">{p.tag}</div>
                  <div className="mt-6">
                    <a href="#executive-access"
                       data-testid={`franchise-cta-${p.code.replace(/[^A-Za-z0-9]/g, '-').toLowerCase()}`}
                       onClick={(e) => { e.preventDefault(); document.getElementById('executive-access')?.scrollIntoView({ behavior: 'smooth' }); }}
                       className="btn-outline-cb !py-2.5 !px-4 !text-[10px]">
                      View Project <ArrowRight size={12} />
                    </a>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-14">
          <button type="button"
                  data-testid="franchise-universe-slate"
                  onClick={() => setActiveArt({ src: "/assets/art/franchise-universe-slate.jpg", label: "CHERUBIM UNIVERSE", sub: "One World. Connected Stories. An Expanding Legacy." })}
                  aria-label="Enlarge the Cherubim Universe slate artwork"
                  className="block w-full cursor-pointer group text-left">
            <div className="border border-[rgba(197,160,89,0.35)] bg-[#0C0C0E] p-[6px] transition-shadow duration-[400ms] ease-out group-hover:border-[rgba(226,195,122,0.65)] group-hover:shadow-[0_24px_50px_-24px_rgba(0,0,0,0.9),0_0_34px_rgba(184,138,44,0.14)]">
              <img src="/assets/art/franchise-universe-slate.jpg"
                   alt="The Cherubim Universe slate — Cherubim, RSM, Seraphim, Reyes, Legend of the Iron Palm, and Precious Metals: Silver & Gold"
                   loading="lazy" decoding="async"
                   className="block w-full h-auto select-none" draggable="false" />
            </div>
          </button>
        </Reveal>
      </div>

      <ArtLightbox active={activeArt} onClose={() => setActiveArt(null)} testid="franchise-lightbox" />
    </section>
  );
};

/* =========================================================== */
/* ======================= EXECUTIVE ========================= */
/* =========================================================== */

const REASONS = ["Development", "Representation", "Financing", "Distribution", "Press", "Other"];

const ExecutiveAccessSection = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", title_role: "", company: "", email: "", reason: "Development", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/executive/request", form);
      toast.success("Request received. Ashe will reply by email after review.");
      setSubmitted(true);
      setForm({ full_name: "", title_role: "", company: "", email: "", reason: "Development", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const MATERIALS = ["Series Bible", "Character Bible", "World Bible", "Lookbook", "Pitch Deck", "Sizzle Reel", "Franchise Roadmap", "Development Materials", "Character Relationship Maps", "Production Vision Package"];

  return (
    <section id="executive-access" data-testid="section-executive-access"
             className="py-24 border-t border-[var(--cb-divider)]"
             style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(74,31,92,0.20) 0%, transparent 60%), var(--cb-bg)" }}>
      <Reveal className={SECTION_WRAP}>
        <SectionEyebrow>EXECUTIVE BRIEFING CENTER</SectionEyebrow>
        <h2 className="font-headline text-[var(--cb-text)] text-[28px] md:text-[40px] uppercase tracking-[0.04em] text-center mt-5">
          Available By Request
        </h2>
        <p className="font-serif italic text-[var(--cb-text-dim)] text-center mt-4 text-[17px] max-w-[720px] mx-auto">
          Confidential development materials available by request to verified industry executives, representatives, financiers, and production partners.
        </p>
        <Rule />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto mt-10">
          {MATERIALS.map((m) => (
            <div key={m} className="cb-card-static p-4 text-center">
              <div className="font-caps text-[10px] text-[var(--cb-gold)]">{m}</div>
            </div>
          ))}
        </div>

        {!open && !submitted && (
          <div className="text-center mt-12">
            <button data-testid="exec-request-open" onClick={() => setOpen(true)} className="btn-primary-cb">
              <FileLock2 size={14} /> Request Access
            </button>
          </div>
        )}

        {submitted && (
          <div className="text-center mt-12 max-w-xl mx-auto" data-testid="exec-submitted">
            <div className="cb-card p-8">
              <div className="font-caps-wide text-[11px] text-[var(--cb-gold)]">SUBMITTED</div>
              <p className="font-serif text-[17px] text-[var(--cb-text)] mt-4 leading-relaxed">
                Your request is in review. Ashe will reply by email once verified. Approved partners receive a private link and access code.
              </p>
            </div>
          </div>
        )}

        {open && !submitted && (
          <form data-testid="exec-request-form" onSubmit={submit} className="cb-card p-7 md:p-10 max-w-2xl mx-auto mt-10 space-y-5">
            <Field label="FULL NAME *" testid="exec-name" required value={form.full_name} onChange={update("full_name")} placeholder="Your full name" />
            <Field label="TITLE / ROLE *" testid="exec-title" required value={form.title_role} onChange={update("title_role")} placeholder="e.g., SVP Drama, Manager, Producer" />
            <Field label="COMPANY / ORGANIZATION *" testid="exec-company" required value={form.company} onChange={update("company")} placeholder="Studio · Agency · Fund" />
            <Field label="EMAIL *" testid="exec-email" type="email" required value={form.email} onChange={update("email")} placeholder="you@company.com" />
            <div>
              <div className="font-caps text-[10px] text-[var(--cb-text-dim)] mb-2">REASON FOR INQUIRY *</div>
              <select data-testid="exec-reason" required value={form.reason} onChange={update("reason")} className="cb-select">
                {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <Field label="MESSAGE" testid="exec-message" multiline value={form.message} onChange={update("message")} placeholder="Brief context for your request." />
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <button type="button" onClick={() => setOpen(false)} className="font-caps text-[11px] text-[var(--cb-text-dim)] hover:text-[var(--cb-gold)]" data-testid="exec-cancel">CANCEL</button>
              <button data-testid="exec-submit" type="submit" disabled={loading} className="btn-primary-cb">
                {loading ? "SUBMITTING…" : "SUBMIT REQUEST"}
              </button>
            </div>
          </form>
        )}
      </Reveal>
    </section>
  );
};

/* =========================================================== */
/* ========================= CONTACT ========================= */
/* =========================================================== */
const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. A reply will follow by email.");
      setForm({ name: "", email: "", organization: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="section-contact" className="py-24 border-t border-[var(--cb-divider)]">
      <Reveal className={SECTION_WRAP}>
        <SectionEyebrow>CONTACT</SectionEyebrow>
        <Rule />
        <div className="max-w-[720px] mx-auto mt-10">
          <p className="font-serif text-[17px] text-[var(--cb-text-dim)] text-center">
            Production Partnerships · Representation · Development Opportunities · Licensing · Investment Inquiries
          </p>

          <form data-testid="contact-form" onSubmit={submit} className="cb-card p-7 md:p-10 mt-10 space-y-5">
            <Field label="NAME *" testid="contact-name" required value={form.name} onChange={update("name")} placeholder="Your name" />
            <Field label="EMAIL *" testid="contact-email" type="email" required value={form.email} onChange={update("email")} placeholder="you@email.com" />
            <Field label="ORGANIZATION" testid="contact-org" value={form.organization} onChange={update("organization")} placeholder="Optional" />
            <Field label="MESSAGE *" testid="contact-message" multiline required value={form.message} onChange={update("message")} placeholder="Tell us how you'd like to connect." />
            <div className="flex justify-end pt-2">
              <button data-testid="contact-submit" type="submit" disabled={loading} className="btn-primary-cb">
                {loading ? "SENDING…" : "SEND MESSAGE"}
              </button>
            </div>
          </form>

          <div className="text-center mt-12" data-testid="contact-listing">
            <div className="font-caps-wide text-[11px] text-[var(--cb-gold)]">ASHECRAFT INTERNATIONAL</div>
            <div className="font-caps-wide text-[11px] text-[var(--cb-text)] mt-3">ASHTON D. BROWN JR. · CREATOR / EXECUTIVE PRODUCER</div>
            <div className="mt-3 space-y-1.5">
              <div>
                <a href="mailto:contact@cherubimseries.com" data-testid="contact-email-link"
                   className="font-caps-wide text-[11px] text-[var(--cb-text-dim)] hover:text-[var(--cb-gold)] transition-colors duration-300">
                  contact@cherubimseries.com
                </a>
              </div>
              <div>
                <a href="https://instagram.com/watchcherubim" target="_blank" rel="noopener noreferrer" data-testid="contact-instagram"
                   className="font-caps-wide text-[11px] text-[var(--cb-text-dim)] hover:text-[var(--cb-gold)] transition-colors duration-300">
                  Instagram · @watchcherubim
                </a>
              </div>
            </div>
            <div className="font-serif italic text-[var(--cb-text-dim)] mt-5 text-[15px]">Truth Has A Guardian.</div>
          </div>
        </div>
      </Reveal>
    </section>
  );
};

const Field = ({ label, testid, type = "text", multiline = false, ...props }) => (
  <div>
    <div className="font-caps text-[10px] text-[var(--cb-text-dim)] mb-2">{label}</div>
    {multiline ? (
      <textarea data-testid={testid} rows={5} className="cb-input resize-y" {...props} />
    ) : (
      <input data-testid={testid} type={type} className="cb-input" {...props} />
    )}
  </div>
);

export default Home;
